import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { Subject, auditTime, filter, takeUntil } from 'rxjs';

type TimelineMode = 'date' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

interface TaskRecord {
  [key: string]: unknown;
}

interface TaskItem {
  name: string;
  dataLabel: string;
  rowLabel: string;
  selectionKey: string;
  colorKey: string;
  group: string;
  segment: string;
  engineModel: string;
  status: string;
  owner: string;
  start: number;
  end: number;
  progress: number | null;
  color: string;
}

interface LegendItem {
  label: string;
  color: string;
}

interface LegendConfigItem {
  label: string;
  color: string;
  keys?: string[];
}
type LineStyleLegendKey = 'solid' | 'dotted';

interface SideTreeModel {
  key: string;
  label: string;
  count: number;
  collapsed: boolean;
}

interface SideTreeSegment {
  key: string;
  label: string;
  count: number;
  collapsed: boolean;
  models: SideTreeModel[];
}
interface QuarterLabelMarker {
  xAxis: number;
  label: string;
}
interface TimelineHeaderMarker {
  left: number;
  label: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_CHART_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#ef4444', // red
];
const FILTER_KEY_ALIASES: Record<string, string> = {
  total_investment_range: 'investment_estimate',
  project_score: 'final_score',
};
const TREE_FOCUS_DIMMED_OPACITY = 0.18;
const RANGE_FILTER_KEYS = new Set([
  'investment_estimate',
  'final_score',
  'simulation_investment_estimate',
  'simulation_final_score',
]);
const COMMON_SIDE_TREE_SEGMENT_KEY_LABEL = 'Common';
const COMMON_SIDE_TREE_SEGMENT_DISPLAY_LABEL = 'Cross Segment';
const DEFAULT_SEGMENT_SORT_ORDER = ['Mining', 'Marine', 'Rail', 'O&G', 'Defense', 'Cross Segment'];
const SEGMENT_PASTEL_ROW_COLORS: Record<string, string> = {
  mining: '#faf5d2',
  marine: '#ecf7fd',
  rail: '#f1faea',
  defense: '#f3f1fc',
  'oil&gas': '#fff1ea',
  crossplatform: '#b7e7f4',
};
const DEFAULT_SEGMENT_ROW_COLOR = 'rgba(148,163,184,0.08)';

@Component({
  selector: 'app-gantt-chart',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss'],
})
export class GanttChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  @ViewChild('chartHost', { static: true }) chartHost!: ElementRef<HTMLDivElement>;
  @ViewChild('chartShell') chartShell?: ElementRef<HTMLDivElement>;
  @ViewChild('fullscreenContainer') fullscreenContainer?: ElementRef<HTMLDivElement>;

  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  protected hasError = false;
  protected errorMessage = '';
  protected resolvedHeight = '420px';
  protected resolvedChartContentHeight = '420px';
  protected resolvedChartContentWidth = '1200px';
  public resolvedChartBodyHeight = '386px';
  protected resolvedCardHeight = 'calc(420px + 120px)';
  protected timelineLabel = 'Monthly Timeline';
  protected isFullscreen = false;
  protected isSideTreeCollapsed = false;
  protected rangeLabel = '';
  protected taskCount = 0;
  protected legendItems: LegendItem[] = [];
  protected selectedLegendLabels: string[] = [];
  protected selectedLineStyleLegendKeys: LineStyleLegendKey[] = [];
  protected sideTree: SideTreeSegment[] = [];
  protected showSideTree = false;
  public timelineHeaderMarkers: TimelineHeaderMarker[] = [];
  public timelineHeaderScrollLeft = 0;
  protected showChartInfoTooltip = false;
  protected chartInfoTooltip =
    'Filters in the below panel are only for visualization of project distribution across segments; investment numbers (on top of the screen) will not get updated.';

  private configService = inject(ConfigService);
  private filterState = inject(GlobalService);
  private globalFilterState = inject(GlobalFilterService);
  private allTasks: TaskItem[] = [];
  private currentMode: TimelineMode = 'monthly';
  private currentDomain: { min: number; max: number } | null = null;
  private destroy$ = new Subject<void>();
  private activeFilters: Record<string, unknown> = {};
  private activeGlobalServiceFilters: Record<string, unknown> = {};
  private activeGlobalFilterServiceFilters: Record<string, unknown> = {};
  private collapsedSegmentKeys = new Set<string>();
  private collapsedModelKeys = new Set<string>();
  private selectedSegmentKeys = new Set<string>();
  private activeSegmentKey: string | null = null;
  private activeModelKey: string | null = null;
  private selectedTaskKey: string | null = null;
  private chartEventsBound = false;
  private labelDebugRenderCount = 0;
  private lastRenderDomain: { min: number; max: number } | null = null;
  private lastRenderedMode: TimelineMode | null = null;
  private lastRenderedUpperMode: TimelineMode | null = null;
  private lastTitleOptionRows: TaskRecord[] = [];
  private hasPublishedProjectTitleOptionsFromApi = false;
  private hasRequestedProjectTitleOptions = false;
  private hasCompletedInitialLoad = false;

  ngAfterViewInit(): void {
    this.activeGlobalServiceFilters = this.extractGlobalServiceFilters(
      this.filterState.getCurrentFilters(),
    );
    this.activeGlobalFilterServiceFilters = this.extractGlobalFilterServiceFilters(
      this.globalFilterState.getCurrentFilters(),
    );
    this.activeFilters = this.mergeActiveFilters();
    this.loadAndRenderChart(this.activeFilters);

    this.filterState.filters$
      .pipe(
        auditTime(300),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => {
        if (filters?.widgetId === this.data?.widgetId) {
          return;
        }
        this.activeGlobalServiceFilters = this.extractGlobalServiceFilters(filters);
        this.activeFilters = this.mergeActiveFilters();
        this.loadAndRenderChart(this.activeFilters);
      });

    this.globalFilterState.filter$
      .pipe(
        auditTime(150),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => {
        if (filters?.widgetId === this.data?.widgetId) {
          return;
        }
        this.activeGlobalFilterServiceFilters = this.extractGlobalFilterServiceFilters(filters);
        this.activeFilters = this.mergeActiveFilters();
        this.loadAndRenderChart(this.activeFilters);
      });

    this.filterState.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetInteractiveState();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.myChart?.dispose();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.refreshTimelineWidthForViewport();
    this.myChart?.resize();
    this.updateTimelineHeaderMarkersFromChart();
    this.syncTimelineHeaderScroll();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const host = this.fullscreenContainer?.nativeElement;
    const activeElement = document.fullscreenElement as HTMLElement | null;
    this.isFullscreen = !!host && !!activeElement && (host === activeElement || host.contains(activeElement));
    if (!this.isFullscreen) {
      this.isSideTreeCollapsed = false;
    }
    setTimeout(() => {
      this.refreshTimelineWidthForViewport();
      this.myChart?.resize();
      this.updateTimelineHeaderMarkersFromChart();
      this.syncTimelineHeaderScroll();
    }, 0);
  }

  protected async toggleFullscreen(): Promise<void> {
    const host = this.fullscreenContainer?.nativeElement;
    if (!host) {
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      await host.requestFullscreen();
    } catch {
      this.isFullscreen = false;
    }
  }

  protected isSideTreeVisible(): boolean {
    return this.showSideTree && (!this.isFullscreen || !this.isSideTreeCollapsed);
  }

  protected toggleSideTreeCollapse(): void {
    if (!this.showSideTree) {
      return;
    }
    this.isSideTreeCollapsed = !this.isSideTreeCollapsed;
    setTimeout(() => {
      this.refreshTimelineWidthForViewport();
      this.myChart?.resize();
      this.syncTimelineHeaderScroll();
    }, 0);
  }

  private refreshTimelineWidthForViewport(): void {
    if (!this.chartHost?.nativeElement || !this.lastRenderDomain) {
      return;
    }

    this.resolvedChartContentWidth = this.resolveTimelineContentWidth(
      this.lastRenderDomain,
      this.currentMode,
    );
    this.chartHost.nativeElement.style.width = this.resolvedChartContentWidth;
  }

  private bindChartEvents(): void {
    if (!this.myChart || this.chartEventsBound) {
      return;
    }

    this.myChart.on('click', (params: any) => {
      const task = params?.data?.taskData as TaskItem | undefined;
      if (!task?.selectionKey) {
        return;
      }

      this.selectedTaskKey =
        this.selectedTaskKey === task.selectionKey ? null : task.selectionKey;
      if (this.selectedTaskKey) {
        this.activeSegmentKey = null;
        this.activeModelKey = null;
      }
      this.renderVisibleTasks();
    });

    this.myChart.on('datazoom', (params: any) => {
      this.refreshQuarterDividersForZoom(params);
    });

    this.chartEventsBound = true;
  }

  protected loadAndRenderChart(filters?: Record<string, unknown>): void {
    if (!this.myChart) {
      this.myChart = echarts.init(this.chartHost.nativeElement);
      this.bindChartEvents();
    }

    this.setProductRoadmapLoadingForInitialFetch(true);
    this.myChart.showLoading();
    this.hasPublishedProjectTitleOptionsFromApi = false;
    this.loadProjectTitleDropdownOptions();

    if (this.data?.apiUrl) {
      if (this.data?.requestPayload) {
        const payload = this.buildRequestPayload(filters);
        this.configService
          .customApi(this.data.apiUrl, payload, this.data, this.data?.method || 'post')
          .subscribe({
            next: (response) => this.renderResolvedApiChart(response, filters),
            error: () => this.showError('Unable to load roadmap timeline data.'),
          });
        return;
      }

      if (this.data?.functionName) {
        this.configService
          .fetchData(
            this.data.apiUrl,
            this.data.functionName,
            {
               ...(filters || {}),
              useQueryParams: this.data?.useQueryParams || this.data?.useQueryParam || false,
            },
            this.data,
            this.data?.method || 'post',
          )
          ?.subscribe({
            next: (response) => this.renderResolvedApiChart(response, filters),
            error: () => this.showError('Unable to load roadmap timeline data.'),
          });
        return;
      }

      this.configService
        .getConfig(
          this.data.apiUrl,
          {
            ...(filters || {}),
            useQueryParams: this.data?.useQueryParams || this.data?.useQueryParam || false,
          },
          this.data?.method,
        )
        ?.subscribe({
          next: (response) => this.renderChart(response, filters),
          error: () => this.showError('Unable to load roadmap timeline data.'),
        });
      return;
    }

    this.renderChart(this.data?.data ?? this.data, filters);
  }

  private loadProjectTitleDropdownOptions(): void {
    const apiUrl = this.data?.titleOptionsApiUrl;
    const functionName = this.data?.titleOptionsFunctionName;
    const requestPayload = this.data?.titleOptionsRequestPayload;

    if (!apiUrl || !functionName || !requestPayload || this.hasRequestedProjectTitleOptions) {
      return;
    }

    this.hasRequestedProjectTitleOptions = true;
    this.configService
      .customApi(apiUrl, requestPayload, this.data, this.data?.titleOptionsMethod || 'post')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.publishProjectTitleOptionsFromResponse(response),
        error: () => {
          this.hasRequestedProjectTitleOptions = false;
          if (!this.hasPublishedProjectTitleOptionsFromApi && this.lastTitleOptionRows.length) {
            this.publishProjectTitleOptions(this.lastTitleOptionRows);
          }
        },
      });
  }

  private renderResolvedApiChart(response: unknown, filters?: Record<string, unknown>): void {
    const resolved = this.resolveResponseData(response);
    const fallback = this.data?.data ?? this.data;
    const source = this.extractRows(resolved).length ? resolved : fallback;
    this.renderChart(source, filters);
  }

  private buildRequestPayload(filters?: Record<string, unknown>): Record<string, unknown> {
    const requestPayload = this.data?.requestPayload;
    if (!requestPayload || typeof requestPayload !== 'object') {
      return {};
    }

    const clonedPayload = JSON.parse(JSON.stringify(requestPayload)) as Record<string, unknown>;

    const normalizedFilters = this.remapFilterKeys(filters || {});

    if (
      !this.data?.mergeActiveFilters ||
      !normalizedFilters ||
      !Object.keys(normalizedFilters).length
    ) {
      return clonedPayload;
    }

    const requestData = (clonedPayload['requestData'] ?? {}) as Record<string, unknown>;
    const payloadFilter = (requestData['filter'] ?? {}) as Record<string, unknown>;

    const segmentFilterKey = this.resolveSegmentFilterKey();
    const preserveAllSegments = this.shouldPreserveAllSegmentsInTree();

    Object.entries(normalizedFilters).forEach(([key, rawValue]) => {
      if (preserveAllSegments && key === segmentFilterKey) {
        return;
      }
      const payloadValue = this.extractPayloadFilterValue(key, rawValue);
      if (!payloadValue) {
        return;
      }
      payloadFilter[key] = payloadValue;
    });

    requestData['filter'] = payloadFilter;
    clonedPayload['requestData'] = requestData;
    return clonedPayload;
  }

  private extractPayloadFilterValue(key: string, value: unknown): unknown[][] | null {
    const tokens = this.extractNormalizedTokens(value);
    if (!tokens.length) {
      return null;
    }

    if (RANGE_FILTER_KEYS.has(key) && tokens.length >= 2) {
      const first = Number(tokens[0]);
      const second = Number(tokens[1]);
      if (Number.isFinite(first) && Number.isFinite(second)) {
        return [[first], [second]];
      }
    }

    return [tokens];
  }

  private extractNormalizedTokens(value: unknown): unknown[] {
    if (!Array.isArray(value)) {
      if (value === null || value === undefined) {
        return [];
      }
      if (typeof value === 'string') {
        const token = value.trim();
        return token ? [token] : [];
      }
      return [value];
    }

    const normalized = value
      .flat(Infinity)
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter((item) => item !== null && item !== undefined && item !== '');

    const unique: unknown[] = [];
    const seen = new Set<string>();

    normalized.forEach((item) => {
      const signature = `${typeof item}:${String(item)}`;
      if (!seen.has(signature)) {
        seen.add(signature);
        unique.push(item);
      }
    });

    return unique;
  }

  private resolveResponseData(response: unknown): unknown {
    const responsePath = this.data?.responsePath as string | undefined;
    if (responsePath && typeof responsePath === 'string') {
      const resolved = this.byPath(response, responsePath);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    // Prefer explicit simulation roadmap payload locations before generic fallbacks.
    const roadmapCandidates = [
      'responseList.0.simulation_roadmap',
      'responseList.0.responseData.0.simulation_roadmap',
      'responseData.0.simulation_roadmap',
      'responseData.simulation_roadmap',
      'simulation_roadmap',
    ];

    for (const path of roadmapCandidates) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const defaultCandidates = [
      'responseList.0.responseData.0',
      'responseList.0.responseData',
      'responseData.0',
      'responseData',
      'data',
    ];

    for (const path of defaultCandidates) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return response;
  }

  protected renderChart(source: unknown, filters?: Record<string, unknown>): void {
    const rows = this.extractRows(source);
    const filteredRows = this.applyRowFilters(rows, filters);
    this.lastTitleOptionRows = filteredRows;
    if (!this.data?.titleOptionsApiUrl) {
      this.publishProjectTitleOptions(filteredRows);
    }
    const tasks = this.normalizeTasks(filteredRows);
    this.logTaskLabelDebug(filteredRows, tasks);
    if (!tasks.length) {
      this.showError(filters ? 'No data for that record.' : 'No roadmap items available for the gantt chart.');
      return;
    }

    // Reset tree visibility state on each fresh render so the default view always shows all bars.
    this.collapsedSegmentKeys.clear();
    this.collapsedModelKeys.clear();
    this.selectedSegmentKeys.clear();
    this.activeSegmentKey = null;
    this.activeModelKey = null;
    this.selectedTaskKey = null;
    this.allTasks = tasks;
    this.currentMode = this.resolveTimelineMode();
    this.currentDomain = this.resolveDomain(tasks, this.currentMode);
    this.legendItems = this.resolveLegendItems(tasks);
    this.allSeriesNames = this.legendItems.map((item) => item.label);
    this.selectedLegendLabels = this.selectedLegendLabels.filter((label) =>
      this.legendItems.some((item) => item.label === label),
    );
    if (this.legendItems.length && !this.selectedLegendLabels.length) {
      this.selectedLegendLabels = this.legendItems.map((item) => item.label);
    }

    if (this.data?.chartConfig?.showLineStyleLegend) {
      this.selectedLineStyleLegendKeys = this.selectedLineStyleLegendKeys.filter(
        (style) => style === 'solid' || style === 'dotted',
      );
      if (!this.selectedLineStyleLegendKeys.length) {
        this.selectedLineStyleLegendKeys = ['solid', 'dotted'];
      }
    } else {
      this.selectedLineStyleLegendKeys = [];
    }

    this.timelineLabel = this.getTimelineLabel(this.currentMode);
    this.rangeLabel = `${this.formatDate(this.currentDomain.min)} to ${this.formatDate(this.currentDomain.max)}`;
    this.hasError = false;
    this.errorMessage = '';

    this.autoCollapseSegmentsByFilter(filters);
    this.renderVisibleTasks();
  }

  private publishProjectTitleOptions(rows: TaskRecord[]): void {
    const titleKey = 'title';
    const titles = Array.from(
      new Set(
        rows
          .map((row) => this.readString(row, titleKey, ['title', 'name', 'label', 'task']))
          .filter((value) => !!value),
      ),
    );

    this.configService.setGanttDropdownOptions({
      filterKey: 'title',
      values: titles,
      widgetId: this.data?.widgetId,
    });
  }

  private publishProjectTitleOptionsFromResponse(response: unknown): void {
    const titles = this.extractProjectTitleOptions(response);
    if (!titles.length) {
      return;
    }

    this.hasPublishedProjectTitleOptionsFromApi = true;
    this.configService.setGanttDropdownOptions({
      filterKey: 'title',
      values: titles,
      widgetId: this.data?.widgetId,
    });
  }

  private extractProjectTitleOptions(source: unknown): string[] {
    const resolved = this.resolveTitleOptionsResponse(source);
    return Array.from(new Set(this.extractTitleValues(resolved)));
  }

  private resolveTitleOptionsResponse(response: unknown): unknown {
    const responsePath = this.data?.titleOptionsResponsePath as string | undefined;
    if (responsePath && typeof responsePath === 'string') {
      const resolved = this.byPath(response, responsePath);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    const titleCandidates = [
      'responseList.0.responseData.0.title',
      'responseList.0.responseData.0.titles',
      'responseList.0.responseData.0',
      'responseData.0.title',
      'responseData.0.titles',
      'responseData.0',
      'responseData.title',
      'responseData.titles',
      'data.title',
      'data.titles',
      'title',
      'titles',
      'data',
    ];

    for (const path of titleCandidates) {
      const resolved = this.byPath(response, path);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return response;
  }

  private extractTitleValues(source: unknown): string[] {
    if (Array.isArray(source)) {
      return source.flatMap((item) => this.extractTitleValues(item));
    }

    if (!source || typeof source !== 'object') {
      const value = String(source ?? '').trim();
      return value ? [value] : [];
    }

    const record = source as Record<string, unknown>;
    const directCandidates = [
      record['title'],
      record['titles'],
      record['name'],
      record['label'],
      record['task'],
      record['value'],
      record['items'],
      record['options'],
      record['rows'],
      record['data'],
    ];

    for (const candidate of directCandidates) {
      const values = this.extractTitleValues(candidate);
      if (values.length) {
        return values;
      }
    }

    const rows = this.extractRows(record);
    if (rows.length) {
      return rows
        .map((row) => this.readString(row, 'title', ['title', 'name', 'label', 'task']))
        .filter((value): value is string => !!value);
    }

    return [];
  }

  private showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.rangeLabel = '';
    this.taskCount = 0;
    this.legendItems = [];
    this.selectedLegendLabels = [];
    this.sideTree = [];
    this.showSideTree = false;
    this.isSideTreeCollapsed = false;
    this.resolvedChartContentHeight = this.resolvedHeight;
    this.resolvedChartContentWidth = '1200px';
    this.activeSegmentKey = null;
    this.activeModelKey = null;
    this.selectedSegmentKeys.clear();
    this.selectedTaskKey = null;
    this.allCategories = [];
    this.allTasks = [];
    this.myChart?.clear();
    this.myChart?.hideLoading();
    this.setProductRoadmapLoadingForInitialFetch(false);
  }

  private extractRows(source: unknown): TaskRecord[] {
    if (Array.isArray(source)) {
      const records = source.filter((item): item is TaskRecord => !!item && typeof item === 'object');
      if (!records.length) {
        return [];
      }

      // Support wrapped API payloads where roadmap rows are nested in each array element
      // (for example under data/categories) instead of being direct task rows.
      const nestedRows = records.flatMap((item) => this.extractRows(item));
      if (nestedRows.length) {
        return nestedRows;
      }

      return records;
    }
    if (!source || typeof source !== 'object') {
      return [];
    }

    const record = source as Record<string, unknown>;
    const taskPath = this.data?.chartConfig?.taskPath || this.data?.taskPath || this.data?.dataPath;
    const directCandidates = [
      typeof taskPath === 'string' && taskPath ? this.byPath(record, taskPath) : undefined,
      record['tasks'],
      record['items'],
      record['rows'],
      this.byPath(record, 'roadmap.tasks'),
      this.byPath(record, 'data.tasks'),
      this.byPath(record, 'data.data.tasks'),
      record['data'],
    ];

    for (const candidate of directCandidates) {
      if (Array.isArray(candidate)) {
        return candidate.filter((item): item is TaskRecord => !!item && typeof item === 'object');
      }
    }

    const groupedRows = this.extractRowsFromCategories(record);
    if (groupedRows.length) {
      return groupedRows;
    }

    const nestedData = record['data'];
    if (nestedData && typeof nestedData === 'object') {
      return this.extractRows(nestedData);
    }

    return [];
  }

  private extractRowsFromCategories(record: Record<string, unknown>): TaskRecord[] {
    const categoryPath = this.data?.chartConfig?.categoryPath || this.data?.categoryPath;
    const categoryCandidates = [
      typeof categoryPath === 'string' && categoryPath
        ? this.byPath(record, categoryPath)
        : undefined,
      record['categories'],
      record['groups'],
      record['tracks'],
      this.byPath(record, 'roadmap.categories'),
      this.byPath(record, 'data.categories'),
      this.byPath(record, 'data.data.categories'),
    ];

    for (const candidate of categoryCandidates) {
      if (!Array.isArray(candidate)) {
        continue;
      }

      const rows = candidate.flatMap((category) => this.rowsFromCategory(category));
      if (rows.length) {
        return rows;
      }
    }

    return [];
  }

  private rowsFromCategory(category: unknown): TaskRecord[] {
    if (!category || typeof category !== 'object') {
      return [];
    }

    const categoryRecord = category as TaskRecord;
    const taskPath =
      this.data?.chartConfig?.categoryTaskPath ||
      this.data?.categoryTaskPath ||
      this.data?.taskPath;
    const taskCandidates = [
      typeof taskPath === 'string' && taskPath ? this.byPath(categoryRecord, taskPath) : undefined,
      categoryRecord['tasks'],
      categoryRecord['items'],
      categoryRecord['rows'],
      categoryRecord['children'],
    ];

    const groupName = this.readString(
      categoryRecord,
      this.data?.chartConfig?.categoryNameKey || this.data?.categoryNameKey,
      ['name', 'group', 'track', 'stream', 'category', 'label'],
    );

    for (const candidate of taskCandidates) {
      if (!Array.isArray(candidate)) {
        continue;
      }

      return candidate
        .filter((item): item is TaskRecord => !!item && typeof item === 'object')
        .map((item) => ({ __parentGroup: groupName, ...item }));
    }

    return [];
  }

  private normalizeTasks(rows: TaskRecord[]): TaskItem[] {
    const colorMap = (this.data?.chartConfig?.colorMap || {}) as Record<string, string>;
    const colorBy = String(this.data?.chartConfig?.colorBy || 'status').trim();
    const taskNameKey = this.resolveConfiguredKey('taskNameKey');
    const startKey = this.resolveConfiguredKey('startKey');
    const endKey = this.resolveConfiguredKey('endKey');
    const groupKey = this.resolveConfiguredKey('groupKey');
    const statusKey = this.resolveConfiguredKey('statusKey');
    const ownerKey = this.resolveConfiguredKey('ownerKey');
    const progressKey = this.resolveConfiguredKey('progressKey');
    const configuredColorKey = this.resolveConfiguredKey('colorKey');
    const colorIndex = new Map<string, number>();

    const tasks = rows
      .map((row) => {
        const name = this.readString(row, taskNameKey, [
          'task',
          'name',
          'label',
          'title',
        ]);
        let start = this.parseDate(
          this.readValue(row, startKey, ['startDate', 'start', 'from']),
          false,
        );
        let end = this.parseDate(
          this.readValue(row, endKey, ['endDate', 'end', 'to']),
          true,
        );

        // If source only has an end/target date, still render as a milestone bar.
        if (start === null && end !== null) {
          start = this.startOfDay(end);
        }

        if (!name || start === null) {
          return null;
        }

        if (end === null || end <= start) {
          end = start + DAY_MS;
        }

        const segment = this.readString(
          row,
          this.data?.chartConfig?.segmentKey || this.data?.segmentKey,
          [
          'cleaned_vertical',
          'segment',
          '__parentGroup',
          'group',
          'track',
          'stream',
          'category',
          ],
        );
        const engineModel = this.readString(
          row,
          this.data?.chartConfig?.engineModelKey || this.data?.engineModelKey,
          [
          'engine_model',
          'engineModel',
          'model',
          'platform',
          ],
        );
        const group = this.readString(row, groupKey, [
          'group',
          'track',
          'stream',
          'category',
          '__parentGroup',
        ]) || [segment, engineModel].filter((value) => !!value).join(' | ');
        const status = this.readString(row, statusKey, ['status', 'phase', 'state']);
        const owner = this.readString(row, ownerKey, ['owner', 'team', 'assignee']);
        const progress = this.parseProgress(
          this.readValue(row, progressKey, ['progress', 'completion']),
        );
        const dataLabel = this.resolveTaskBarLabel(row, { name, group, status, owner, progress });
        const explicitColor = this.readString(row, configuredColorKey, ['color', 'barColor']);
        const colorKey = this.resolveColorKey(row, colorBy, { name, group, status, owner });

        if (!colorIndex.has(colorKey)) {
          colorIndex.set(colorKey, colorIndex.size);
        }

        const defaultColor = this.getDefaultColor(colorIndex.get(colorKey) || 0);
        const resolvedColor =
          explicitColor || this.resolveColorMapValue(colorMap, colorKey) || defaultColor;

        return {
          name,
          dataLabel,
          rowLabel: this.buildRowLabel(name, group, engineModel),
          selectionKey: this.buildSelectionKey(name, group, engineModel),
          colorKey,
          group,
          segment: segment || group || 'Other',
          engineModel: engineModel || 'Unspecified',
          status,
          owner,
          start,
          end,
          progress,
          color: resolvedColor,
        };
      })
      .filter((task): task is TaskItem => task !== null);

    if (this.data?.chartConfig?.sortTasks !== false) {
      tasks.sort((a, b) => this.compareTasksForDisplay(a, b));
    }

    const uniqueRowLabels = this.uniquify(tasks.map((task) => task.rowLabel));
    const uniqueSelectionKeys = this.uniquify(tasks.map((task) => task.selectionKey));

    return tasks.map((task, index) => ({
      ...task,
      rowLabel: uniqueRowLabels[index],
      selectionKey: uniqueSelectionKeys[index],
    }));
  }

  private buildOption(
    tasks: TaskItem[],
    mode: TimelineMode,
    domain: { min: number; max: number },
  ): echarts.EChartsOption {
    const withSideTree = this.useSideTreeMode();
    // In side-tree mode y-axis labels are hidden, so keep timeline left padding minimal.
    const configuredSideTreeLeft = Number(this.data?.chartConfig?.sideTreeTimelineLeft);
    const sideTreeLeft =
      Number.isFinite(configuredSideTreeLeft) && configuredSideTreeLeft >= 0
        ? configuredSideTreeLeft
        : 18;
    const left = withSideTree ? sideTreeLeft : this.resolveLeft(tasks);
    const showProgressLabel = this.data?.chartConfig?.showProgressLabel !== false;
    const useProgressTrack = this.data?.chartConfig?.showProgressTrack !== false;
    const tooltipConfig = this.buildTooltipConfig(this.data?.chartConfig?.tooltip);
    const taskLabelFontSize = this.resolveTaskLabelFontSize();
    const axisPosition =
      String(this.data?.chartConfig?.axisPosition || 'top').toLowerCase() === 'bottom'
        ? 'bottom'
        : 'top';
    const upperMode = axisPosition === 'top' ? this.resolveUpperTimelineMode(mode) : null;
    const responsiveUpperMode = this.resolveResponsiveUpperTimelineMode(upperMode);
    const hasUpperAxis = !!responsiveUpperMode;
    const showSplitLine =
      !this.shouldUseQuarterDividersAsPrimaryLineSource(mode, responsiveUpperMode);
    const gridTop =
      axisPosition === 'top'
        ? this.shouldUseExternalTimelineHeader()
          ? 4
          : this.resolveTimelineGridPadding(axisPosition, hasUpperAxis)
        : 18;
    const gridBottom =
      axisPosition === 'bottom'
        ? this.resolveTimelineGridPadding(axisPosition, hasUpperAxis)
        : 40;
    const verticalDataZoom = this.buildVerticalDataZoom(tasks.length, gridTop, gridBottom);
    const horizontalDataZoom = this.buildHorizontalDataZoom(hasUpperAxis);
    const hasVerticalDataZoom = verticalDataZoom.length > 0;
    const rowBandColors = this.resolveSegmentRowBandColors(tasks);

    this.lastRenderedMode = mode;
    this.lastRenderedUpperMode = responsiveUpperMode;

    const series: any[] = [
      {
        name: this.data?.seriesName || this.data?.title || 'Roadmap',
        type: 'custom',
        // Keep bars/text above timeline divider lines.
        zlevel: 2,
        z: 3,
        renderItem: (params: any, api: any) =>
          this.renderBarWithLabel(params, api, showProgressLabel, useProgressTrack),
        encode: { x: [1, 2], y: 0 },
        data: tasks.map((task, index) => {
          const isTreeFocused = this.isTaskInActiveTreeFocus(task);
          const normalizedStatus = this.normalizeFilterToken(task.status);
          return {
          isTreeFocused,
          name: task.selectionKey,
          // Keep renderer-critical flags in value[] because api.value(...) is stable in custom renderItem.
          value: [
            index,
            task.start,
            task.end,
            task.progress ?? -1,
            task.dataLabel,
            normalizedStatus,
            isTreeFocused ? 1 : 0,
          ],
          itemStyle: {
            color: task.color,
            opacity: isTreeFocused ? 1 : TREE_FOCUS_DIMMED_OPACITY,
          },
          taskName: task.name,
          taskLabelInBar: task.dataLabel,
          task: task.name,
          category: task.group,
          group: task.group,
          status: task.status,
          owner: task.owner,
          start: task.start,
          end: task.end,
          startDate: this.formatDate(task.start),
          endDate: this.formatDate(task.end),
          duration: this.formatDuration(task.start, task.end),
          progress: task.progress,
          taskData: task,
        };
        }),
      },
    ];

    const quarterDividerValues = this.resolveQuarterDividerValues(domain, mode, responsiveUpperMode);
    const renderQuarterLabelsBetweenLines = this.shouldRenderQuarterLabelsBetweenLines(
      mode,
      responsiveUpperMode,
    );
    const quarterLabelMarkers = this.resolveQuarterLabelMarkers(domain, mode, responsiveUpperMode);
    const yearDividerValues = this.resolveYearDividerValues(domain, mode, responsiveUpperMode);
    if (yearDividerValues.length) {
      series.push(this.buildYearDividerSeries(yearDividerValues));
    }
    if (quarterDividerValues.length) {
      series.push(this.buildQuarterDividerSeries(quarterDividerValues, !renderQuarterLabelsBetweenLines));
    }
    if (quarterLabelMarkers.length) {
      series.push(this.buildQuarterLabelSeries(quarterLabelMarkers));
    }

    this.timelineHeaderMarkers = this.resolveTimelineHeaderMarkers(domain, left);

    return {
      animationDuration: 400,
      grid: {
        left,
        right: hasVerticalDataZoom ? 42 : 24,
        top: gridTop,
        bottom: gridBottom,
        containLabel: false,
      },
      tooltip: {
        ...tooltipConfig,
        appendToBody: true,
      },
      dataZoom: [...verticalDataZoom, ...horizontalDataZoom],
      xAxis: this.buildTimelineAxes(mode, domain, axisPosition, responsiveUpperMode, showSplitLine),
      yAxis: {
        type: 'category',
        inverse: true,
        data: tasks.map((task) => task.rowLabel),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: !withSideTree,
          width: Math.max(80, left - 24),
          color: '#0f172a',
          fontWeight: 500,
          fontSize: taskLabelFontSize,
          lineHeight: Math.round(taskLabelFontSize * 1.5),
          overflow: 'truncate',
          formatter: (value: string) =>
            this.formatTaskAxisLabel(value, left - 24, taskLabelFontSize),
        },
        splitArea: { show: true, areaStyle: { color: rowBandColors } },
      },
      series,
    };
  }

  private resolveSegmentRowBandColors(tasks: TaskItem[]): string[] {
    if (!tasks.length) {
      return [DEFAULT_SEGMENT_ROW_COLOR];
    }

    return tasks.map((task) => {
      // Keep row-band color source aligned with side-tree segment mapping
      // so "Cross Platform" rows match the segment shown in the tree.
      const sideTreeSegment = this.resolveSideTreeSegmentTokens(task)[0] || task.segment;
      const segmentKey = this.normalizeSegmentRowKey(sideTreeSegment);
      return SEGMENT_PASTEL_ROW_COLORS[segmentKey] ?? DEFAULT_SEGMENT_ROW_COLOR;
    });
  }

  private normalizeSegmentRowKey(rawSegment: string): string {
    const raw = String(rawSegment || '');
    const sourceLower = raw.toLowerCase();
    const normalized = sourceLower
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

    // Allow common data variants/typos for cross-platform labels in chart rows.
    const looksLikeCrossPlatform =
      normalized.includes('crossplatform') ||
      normalized.includes('crosssegment') ||
      normalized.includes('crossplatfrom') ||
      normalized.includes('crossplatfrm') ||
      (sourceLower.includes('cross') && sourceLower.includes('platform')) ||
      (sourceLower.includes('cross') && sourceLower.includes('segment')) ||
      normalized === 'common' ||
      sourceLower.includes('common');

    if (looksLikeCrossPlatform) {
      return 'crossplatform';
    }
    if (
      normalized === 'oilgass' ||
      normalized === 'oilandgas' ||
      normalized === 'oilgas' ||
      normalized === 'og'
    ) {
      return 'oil&gas';
    }
    return normalized;
  }

  public resolveTreeSegmentColor(segment: SideTreeSegment): string {
    const normalized = this.normalizeSegmentRowKey(segment?.label || segment?.key || '');
    return SEGMENT_PASTEL_ROW_COLORS[normalized] ?? DEFAULT_SEGMENT_ROW_COLOR;
  }

  private resolveQuarterDividerValues(
    domain: { min: number; max: number },
    mode: TimelineMode,
    upperMode: TimelineMode | null,
  ): number[] {
    const enabled =
      this.data?.chartConfig?.showQuarterDividerLines === true ||
      this.data?.chartConfig?.showQuarterDividers === true;

    if (!enabled) {
      return [];
    }

    const hasQuarterContext =
      mode === 'quarterly' || mode === 'yearly' || upperMode === 'quarterly' || upperMode === 'yearly';
    if (!hasQuarterContext) {
      return [];
    }

    const values: number[] = [];
    let cursor = this.startOfQuarter(domain.min);

    while (cursor <= domain.max) {
      const month = new Date(cursor).getMonth();
      const isYearBoundary = month === 0;
      if (!isYearBoundary && cursor > domain.min && cursor < domain.max) {
        values.push(cursor);
      }
      cursor = this.addMonths(cursor, 3);
    }

    return values;
  }

  private resolveYearDividerValues(
    domain: { min: number; max: number },
    mode: TimelineMode,
    upperMode: TimelineMode | null,
  ): number[] {
    if (!this.shouldUseQuarterDividersAsPrimaryLineSource(mode, upperMode)) {
      return [];
    }

    const values: number[] = [];
    let cursor = this.startOfYear(domain.min);

    while (cursor <= domain.max) {
      if (cursor > domain.min && cursor < domain.max) {
        values.push(cursor);
      }
      cursor = this.addMonths(cursor, 12);
    }

    return values;
  }

  private shouldUseQuarterDividersAsPrimaryLineSource(
    mode: TimelineMode,
    upperMode: TimelineMode | null,
  ): boolean {
    const enabled =
      this.data?.chartConfig?.showQuarterDividerLines === true ||
      this.data?.chartConfig?.showQuarterDividers === true;
    if (!enabled) {
      return false;
    }

    return (
      mode === 'quarterly' ||
      mode === 'yearly' ||
      upperMode === 'quarterly' ||
      upperMode === 'yearly'
    );
  }

  private buildQuarterDividerSeries(values: number[], showLabels: boolean): any {
    const color = String(this.data?.chartConfig?.quarterDividerLineColor || '#dbe4ef');
    const width = Math.max(1, Number(this.data?.chartConfig?.quarterDividerLineWidth || 1));
    const labelColor = String(this.data?.chartConfig?.quarterDividerLabelColor || '#64748b');
    const labelFontSize = this.resolveFontSize(this.data?.chartConfig?.quarterDividerLabelFontSize, 11);

    return {
      id: 'quarter-dividers',
      name: 'Quarter Dividers',
      type: 'line',
      data: [],
      silent: true,
      symbol: 'none',
      zlevel: 0,
      z: 0,
      xAxisIndex: 0,
      yAxisIndex: 0,
      lineStyle: { opacity: 0 },
      tooltip: { show: false },
      markLine: {
        silent: true,
        animation: false,
        zlevel: 0,
        z: 0,
        symbol: ['none', 'none'],
        label: {
          show: showLabels,
          position: 'start',
          distance: 8,
          color: labelColor,
          fontSize: labelFontSize,
          fontWeight: 600,
        },
        lineStyle: { color, width },
        data: values.map((value) => ({
          xAxis: value,
          label: showLabels
            ? {
                show: true,
                formatter: this.formatQuarterDividerLabel(value),
              }
            : { show: false },
        })),
      },
    };
  }

  private shouldShowQuarterDividerLabels(): boolean {
    return (
      this.data?.chartConfig?.showQuarterDividerLabels === true ||
      this.data?.chartConfig?.showQuarterDividersLabels === true
    );
  }

  private shouldRenderQuarterLabelsBetweenLines(
    mode: TimelineMode,
    upperMode: TimelineMode | null,
  ): boolean {
    return mode === 'quarterly' && upperMode === 'yearly' && this.shouldShowQuarterDividerLabels();
  }

  private formatQuarterDividerLabel(value: number): string {
    const quarter = Math.floor(new Date(value).getMonth() / 3) + 1;
    return `Q${quarter}`;
  }

  private buildYearDividerSeries(values: number[]): any {
    const color = String(this.data?.chartConfig?.yearDividerLineColor || '#cbd5e1');
    const width = Math.max(1, Number(this.data?.chartConfig?.yearDividerLineWidth || 1));

    return {
      id: 'year-dividers',
      name: 'Year Dividers',
      type: 'line',
      data: [],
      silent: true,
      symbol: 'none',
      zlevel: 0,
      z: 1,
      xAxisIndex: 0,
      yAxisIndex: 0,
      lineStyle: { opacity: 0 },
      tooltip: { show: false },
      markLine: {
        silent: true,
        animation: false,
        zlevel: 0,
        z: 1,
        symbol: ['none', 'none'],
        label: { show: false },
        lineStyle: { color, width },
        data: values.map((value) => ({ xAxis: value })),
      },
    };
  }

  private renderBarWithLabel(
    params: any,
    api: any,
    showProgressLabel: boolean,
    useProgressTrack: boolean,
  ): echarts.CustomSeriesRenderItemReturn {
    const index = Number(api.value(0));
    const start = api.coord([api.value(1), index]);
    const end = api.coord([api.value(2), index]);
    const task = params?.data?.taskData as TaskItem | undefined;
    const seriesStyle = api.style() as Record<string, unknown>;
    const baseColor = String(seriesStyle?.['fill'] || task?.color || this.getDefaultColor(index));
    const opacity = Number(seriesStyle?.['opacity'] ?? params?.data?.itemStyle?.opacity ?? 1);
    const encodedTreeFocus = Number(api.value(6));
    const isTreeFocused =
      Number.isFinite(encodedTreeFocus) && encodedTreeFocus > 0
        ? true
        : params?.data?.isTreeFocused !== false;
    const baseBarHeight = Math.max(18, Number(this.data?.chartConfig?.barHeight || 24));
    const emphasizedBarHeight = isTreeFocused ? Math.round(baseBarHeight * 1.1) : baseBarHeight;
    const bandSize = Math.max(8, Math.abs(Number(api.size([0, 1])?.[1] || 0)));
    const rowGap = Math.max(2, Number(this.data?.chartConfig?.barRowGap ?? 4));
    const maxBarHeight = Math.max(6, Math.floor(bandSize - rowGap));
    const barHeight = Math.max(6, Math.min(emphasizedBarHeight, maxBarHeight));
    const progressLabelFontSize = this.resolveProgressLabelFontSize();
    const inBarTaskLabelFontSize = this.resolveInBarTaskLabelFontSize();
    const minTaskLabelWidth = Math.max(
      0,
      Number(this.data?.chartConfig?.minTaskLabelWidthInBar ?? 0),
    );
    const minProgressLabelWidth = Math.max(
      0,
      Number(this.data?.chartConfig?.minProgressLabelWidthInBar ?? 0),
    );
    const rect = echarts.graphic.clipRectByRect(
      {
        x: start[0],
        y: start[1] - barHeight / 2,
        width: Math.max(6, end[0] - start[0]),
        height: barHeight,
      },
      {
        x: params.coordSys.x,
        y: params.coordSys.y,
        width: params.coordSys.width,
        height: params.coordSys.height,
      },
    );
    if (!rect) {
      return null;
    }

    const encodedProgress = Number(api.value(3));
    const progress =
      task?.progress ?? (Number.isFinite(encodedProgress) && encodedProgress >= 0 ? encodedProgress : null);
    const explicitDataLabel = this.stringifyTaskBarLabel(
      task?.dataLabel ?? params?.data?.taskLabelInBar ?? api.value(4),
    );
    const fallbackTaskText = String(
      task?.name || params?.data?.taskName || params?.data?.name || '',
    ).trim();
    const taskText = explicitDataLabel || fallbackTaskText;
    const showTaskLabelInBar =
      !!explicitDataLabel ||
      this.data?.chartConfig?.showTaskLabelInBar !== false &&
      this.data?.chartConfig?.showlabelsInBar !== false &&
      this.data?.chartConfig?.showLabelsInBar !== false;
    const statusToken = this.normalizeFilterToken(api.value(5) ?? params?.data?.status ?? task?.status);
    const isInactive = this.isInactiveByStatus(statusToken);
    const children: any[] = [];
    const horizontalPadding = Math.min(10, Math.max(4, Math.round(rect.height * 0.32)));
    let labelX = rect.x + horizontalPadding;
    let labelWidth = Math.max(8, rect.width - horizontalPadding * 2);
    let labelText = '';
    let labelColor = '#ffffff';
    let labelFontSize = inBarTaskLabelFontSize;

    if (useProgressTrack && progress !== null) {
      const trackRect: any = {
        type: 'rect',
        shape: { ...rect, r: 8 },
        style: {
          fill: this.withAlpha(baseColor, 0.2),
          stroke: this.withAlpha(baseColor, 0.35),
          lineWidth: isTreeFocused ? 2 : 1,
          opacity,
        },
      };
      children.push(trackRect);

      if (progress > 0) {
        const progressRect: any = {
          type: 'rect',
          shape: {
            x: rect.x,
            y: rect.y,
            width: Math.max(6, rect.width * (progress / 100)),
            height: rect.height,
            r: 8,
          },
          style: {
            fill: isInactive ? this.withAlpha(baseColor, 0.22) : baseColor,
            stroke: isInactive ? this.withAlpha(baseColor, 0.98) : '#ffffff',
            lineWidth: isInactive ? 2.2 : (isTreeFocused ? 2 : 1),
            lineDash: isInactive ? [8, 4] : undefined,
            opacity,
          },
        };

        if (explicitDataLabel && progress < 45) {
          labelX =
            rect.x +
            Math.min(
              rect.width - horizontalPadding,
              Math.max(horizontalPadding, rect.width * (progress / 100) + horizontalPadding),
            );
          labelWidth = Math.max(8, rect.width - (labelX - rect.x) - horizontalPadding);
        }

        if (explicitDataLabel) {
          const textColor = progress < 45 ? '#0f172a' : this.getContrastingTextColor(baseColor);
          labelText = this.fitTextToBar(
            explicitDataLabel,
            Math.max(6, labelWidth),
            inBarTaskLabelFontSize,
          );
          labelColor = textColor;
          labelFontSize = inBarTaskLabelFontSize;
        } else if (showTaskLabelInBar && taskText && rect.width > minTaskLabelWidth) {
          const textColor = progress < 55 ? '#0f172a' : this.getContrastingTextColor(baseColor);
          labelText = this.fitTextToBar(
            taskText,
            Math.max(6, rect.width - horizontalPadding * 2),
            inBarTaskLabelFontSize,
          );
          labelColor = textColor;
          labelFontSize = inBarTaskLabelFontSize;
        } else if (showProgressLabel && progress !== null && rect.width > minProgressLabelWidth) {
          labelText = `${progress}%`;
          labelColor = progress >= 45 ? this.getContrastingTextColor(baseColor) : '#0f172a';
          labelFontSize = progressLabelFontSize;
        }

        children.push(progressRect);
      } else if (explicitDataLabel) {
        labelText = this.fitTextToBar(
          explicitDataLabel,
          Math.max(6, labelWidth),
          inBarTaskLabelFontSize,
        );
        labelColor = '#0f172a';
        labelFontSize = inBarTaskLabelFontSize;
      } else if (showTaskLabelInBar && taskText && rect.width > minTaskLabelWidth) {
        labelText = this.fitTextToBar(
          taskText,
          Math.max(6, rect.width - horizontalPadding * 2),
          inBarTaskLabelFontSize,
        );
        labelColor = '#0f172a';
        labelFontSize = inBarTaskLabelFontSize;
      } else if (showProgressLabel && progress !== null && rect.width > minProgressLabelWidth) {
        labelText = `${progress}%`;
        labelColor = '#0f172a';
        labelFontSize = progressLabelFontSize;
      }
    } else {
      const solidBarStyle = {
        ...seriesStyle,
        fill: isInactive ? this.withAlpha(baseColor, 0.22) : baseColor,
        stroke: isInactive ? this.withAlpha(baseColor, 0.98) : '#ffffff',
        lineWidth: isInactive ? 2.2 : (isTreeFocused ? 2 : 1),
        lineDash: isInactive ? [8, 4] : undefined,
        opacity,
      };
      const solidRect: any = {
        type: 'rect',
        shape: { ...rect, r: 8 },
        style: solidBarStyle,
      };

      if (explicitDataLabel) {
        labelText = this.fitTextToBar(
          explicitDataLabel,
          Math.max(6, labelWidth),
          inBarTaskLabelFontSize,
        );
        labelColor = this.getContrastingTextColor(baseColor);
        labelFontSize = inBarTaskLabelFontSize;
      } else if (showTaskLabelInBar && taskText && rect.width > minTaskLabelWidth) {
        labelText = this.fitTextToBar(
          taskText,
          Math.max(6, rect.width - horizontalPadding * 2),
          inBarTaskLabelFontSize,
        );
        labelColor = this.getContrastingTextColor(baseColor);
        labelFontSize = inBarTaskLabelFontSize;
      } else if (showProgressLabel && progress !== null && rect.width > minProgressLabelWidth) {
        labelText = `${progress}%`;
        labelColor = progress >= 45 ? this.getContrastingTextColor(baseColor) : '#0f172a';
        labelFontSize = progressLabelFontSize;
      }

      children.push(solidRect);
    }

    if (labelText && isInactive) {
      labelColor = '#1f2937';
    }

    if (labelText) {
      if (explicitDataLabel && this.labelDebugRenderCount < 3) {
        this.labelDebugRenderCount += 1;
      }

      children.push({
        type: 'text',
        silent: true,
        z2: 20,
        style: {
          x: labelX,
          y: rect.y + rect.height / 2,
          text: labelText,
          align: 'left',
          verticalAlign: 'middle',
          fill: labelColor,
          opacity,
          width: labelWidth,
          overflow: 'truncate',
          font: `600 ${labelFontSize}px "Segoe UI", Arial, sans-serif`,
        },
      });
    }

    return { type: 'group', children };
  }

  private buildTimelineAxes(
    mode: TimelineMode,
    domain: { min: number; max: number },
    axisPosition: 'top' | 'bottom',
    upperMode: TimelineMode | null,
    showLowerSplitLine: boolean,
  ): echarts.EChartsOption['xAxis'] {
    const lowerOffset = upperMode ? 24 : 0;
    const shiftQuarterLabelToPrevious = mode === 'quarterly' && upperMode === 'yearly';
    const hideLowerQuarterLabels =
      mode === 'quarterly' && upperMode === 'yearly' && this.shouldShowQuarterDividerLabels();
    const lowerAxis = this.createTimelineAxis(
      mode,
      domain,
      axisPosition,
      lowerOffset,
      showLowerSplitLine,
      false,
      true,
      shiftQuarterLabelToPrevious,
      hideLowerQuarterLabels,
    );

    if (!upperMode) {
      return lowerAxis;
    }

    const upperAxis = this.createTimelineAxis(
      upperMode,
      domain,
      axisPosition,
      0,
      false,
      true,
      false,
      false,
    );
    return axisPosition === 'top' ? [upperAxis, lowerAxis] : [lowerAxis, upperAxis];
  }

  private createTimelineAxis(
    mode: TimelineMode,
    domain: { min: number; max: number },
    position: 'top' | 'bottom',
    offset: number,
    showSplitLine: boolean,
    emphasizeLabels: boolean,
    showAxisTick: boolean,
    shiftQuarterLabelToPrevious = false,
    forceHideLabels = false,
  ): any {
    const chartWidth = this.getChartWidth();
    const compactLabel = !emphasizeLabels || chartWidth < 640;
    const hideLowerLabels = this.data?.chartConfig?.showLowerTimelineLabels === false && !emphasizeLabels;
    const labelFontSize = this.resolveTimelineLabelFontSize(emphasizeLabels);
    const splitNumber = this.resolveTimelineSplitNumber(mode, emphasizeLabels, chartWidth);
    const hideForExternalHeader = this.shouldUseExternalTimelineHeader();
    return {
      type: 'time',
      min: domain.min,
      max: domain.max,
      minInterval: this.minInterval(mode),
      splitNumber,
      position,
      offset,
      axisLine: hideForExternalHeader ? { show: false } : { lineStyle: { color: '#cbd5e1' } },
      axisTick: {
        show: hideForExternalHeader ? false : showAxisTick,
        length: emphasizeLabels ? 8 : 5,
        lineStyle: { color: '#cbd5e1' },
      },
      axisLabel: {
        show: !hideForExternalHeader && !hideLowerLabels && !forceHideLabels,
        color: emphasizeLabels ? '#0f172a' : '#475569',
        hideOverlap: true,
        margin: emphasizeLabels ? 10 : 12,
        fontWeight: emphasizeLabels ? 600 : 500,
        fontSize: labelFontSize,
        lineHeight: Math.round(labelFontSize * 1.25),
        padding: [0, 4, 0, 4],
        formatter: (value: number) =>
          this.tick(value, mode, compactLabel, shiftQuarterLabelToPrevious),
      },
      splitLine: showSplitLine ? { show: true, lineStyle: { color: '#e2e8f0' } } : { show: false },
    };
  }

  private buildQuarterLabelSeries(markers: QuarterLabelMarker[]): any {
    const labelColor = String(this.data?.chartConfig?.quarterDividerLabelColor || '#64748b');
    const labelFontSize = this.resolveFontSize(this.data?.chartConfig?.quarterDividerLabelFontSize, 11);

    return {
      id: 'quarter-labels-between-lines',
      name: 'Quarter Labels',
      type: 'line',
      data: [],
      silent: true,
      symbol: 'none',
      zlevel: 4,
      z: 5,
      xAxisIndex: 0,
      yAxisIndex: 0,
      lineStyle: { opacity: 0 },
      tooltip: { show: false },
      markLine: {
        silent: true,
        animation: false,
        zlevel: 4,
        z: 5,
        symbol: ['none', 'none'],
        lineStyle: {
          color: 'rgba(0,0,0,0)',
          opacity: 0.001,
          width: 1,
        },
        label: {
          show: true,
          position: 'insideStartTop',
          distance: 10,
          color: labelColor,
          fontSize: labelFontSize,
          fontWeight: 600,
        },
        data: markers.map((marker) => ({
          xAxis: marker.xAxis,
          label: {
            show: true,
            formatter: marker.label,
          },
        })),
      },
    };
  }

  private resolveQuarterLabelMarkers(
    domain: { min: number; max: number },
    mode: TimelineMode,
    upperMode: TimelineMode | null,
  ): QuarterLabelMarker[] {
    if (!this.shouldRenderQuarterLabelsBetweenLines(mode, upperMode)) {
      return [];
    }

    const markers: QuarterLabelMarker[] = [];
    let quarterStart = this.startOfQuarter(domain.min);

    while (quarterStart < domain.max) {
      const nextQuarterStart = this.addMonths(quarterStart, 3);
      const midpoint = quarterStart + (nextQuarterStart - quarterStart) / 2;

      if (midpoint >= domain.min && midpoint <= domain.max) {
        const quarter = Math.floor(new Date(quarterStart).getMonth() / 3) + 1;
        markers.push({
          xAxis: midpoint,
          label: `Q${quarter}`,
        });
      }

      quarterStart = nextQuarterStart;
    }

    return markers;
  }

  private refreshQuarterDividersForZoom(params: any): void {
    if (!this.myChart || !this.lastRenderDomain || !this.lastRenderedMode) {
      return;
    }
    if (!this.isHorizontalZoomEvent(params)) {
      return;
    }
    if (!this.shouldUseQuarterDividersAsPrimaryLineSource(this.lastRenderedMode, this.lastRenderedUpperMode)) {
      return;
    }

    const visibleDomain = this.resolveVisibleDomainFromZoomEvent(params, this.lastRenderDomain);
    if (!visibleDomain) {
      return;
    }

    const quarterDividerValues = this.resolveQuarterDividerValues(
      visibleDomain,
      this.lastRenderedMode,
      this.lastRenderedUpperMode,
    );
    const quarterLabelMarkers = this.resolveQuarterLabelMarkers(
      visibleDomain,
      this.lastRenderedMode,
      this.lastRenderedUpperMode,
    );
    const yearDividerValues = this.resolveYearDividerValues(
      visibleDomain,
      this.lastRenderedMode,
      this.lastRenderedUpperMode,
    );

    const renderQuarterLabelsBetweenLines = this.shouldRenderQuarterLabelsBetweenLines(
      this.lastRenderedMode,
      this.lastRenderedUpperMode,
    );
    const showQuarterLabelsOnDividerLines =
      this.shouldShowQuarterDividerLabels() && !renderQuarterLabelsBetweenLines;

    this.myChart.setOption({
      series: [
        {
          id: 'year-dividers',
          markLine: {
            data: yearDividerValues.map((value) => ({ xAxis: value })),
          },
        },
        {
          id: 'quarter-dividers',
          markLine: {
            data: quarterDividerValues.map((value) => ({
              xAxis: value,
              label: showQuarterLabelsOnDividerLines
                ? {
                    show: true,
                    formatter: this.formatQuarterDividerLabel(value),
                  }
                : { show: false },
            })),
          },
        },
        {
          id: 'quarter-labels-between-lines',
          markLine: {
            data: quarterLabelMarkers.map((marker) => ({
              xAxis: marker.xAxis,
              label: {
                show: true,
                formatter: marker.label,
              },
            })),
          },
        },
      ],
    });
  }

  private isHorizontalZoomEvent(params: any): boolean {
    const payloads = Array.isArray(params?.batch) && params.batch.length ? params.batch : [params];
    let hasHorizontalSignal = false;
    let hasUnknownPayload = false;

    for (const payload of payloads) {
      if (!payload || typeof payload !== 'object') {
        hasUnknownPayload = true;
        continue;
      }

      const dataZoomId = String(payload?.dataZoomId || '').trim().toLowerCase();
      if (dataZoomId.startsWith('gantty')) {
        continue;
      }
      if (dataZoomId.startsWith('ganttx')) {
        hasHorizontalSignal = true;
        continue;
      }

      const hasXAxis = payload.xAxisIndex !== undefined || payload.xAxisId !== undefined;
      const hasYAxis = payload.yAxisIndex !== undefined || payload.yAxisId !== undefined;
      if (hasYAxis && !hasXAxis) {
        continue;
      }
      if (hasXAxis) {
        hasHorizontalSignal = true;
        continue;
      }

      hasUnknownPayload = true;
    }

    return hasHorizontalSignal || hasUnknownPayload;
  }

  private resolveVisibleDomainFromZoomEvent(
    params: any,
    fallbackDomain: { min: number; max: number },
  ): { min: number; max: number } | null {
    const payload = Array.isArray(params?.batch) && params.batch.length ? params.batch[0] : params;
    const startValue = Number(payload?.startValue);
    const endValue = Number(payload?.endValue);

    if (Number.isFinite(startValue) && Number.isFinite(endValue) && endValue > startValue) {
      return {
        min: Math.max(fallbackDomain.min, startValue),
        max: Math.min(fallbackDomain.max, endValue),
      };
    }

    const start = Number(payload?.start);
    const end = Number(payload?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return null;
    }

    const span = Math.max(1, fallbackDomain.max - fallbackDomain.min);
    const min = fallbackDomain.min + (Math.max(0, start) / 100) * span;
    const max = fallbackDomain.min + (Math.min(100, end) / 100) * span;
    return max > min ? { min, max } : null;
  }

  private resolveTimelineMode(): TimelineMode {
    return this.normalizeTimelineMode(
      this.data?.chartConfig?.timelineMode || this.data?.timelineMode || 'monthly',
    );
  }

  private resolveUpperTimelineMode(lowerMode: TimelineMode): TimelineMode | null {
    const configured = this.data?.chartConfig?.upperTimelineMode || this.data?.upperTimelineMode;
    const normalizedConfigured = String(configured || '')
      .trim()
      .toLowerCase();

    if (
      normalizedConfigured === 'none' ||
      normalizedConfigured === 'off' ||
      normalizedConfigured === 'false'
    ) {
      return null;
    }

    if (normalizedConfigured) {
      return this.normalizeTimelineMode(configured);
    }

    if (lowerMode === 'monthly' || lowerMode === 'quarterly') return 'yearly';
    if (lowerMode === 'weekly' || lowerMode === 'date') return 'monthly';
    return null;
  }

  private normalizeTimelineMode(value: unknown): TimelineMode {
    const text = String(value || '').toLowerCase();
    if (text === 'day' || text === 'daily' || text === 'date') return 'date';
    if (text === 'week' || text === 'weekly') return 'weekly';
    if (text === 'month' || text === 'monthly') return 'monthly';
    if (text === 'quarter' || text === 'quarterly' || text === 'qtr') return 'quarterly';
    if (text === 'year' || text === 'yearly') return 'yearly';
    return 'date';
  }

  private resolveDomain(tasks: TaskItem[], mode: TimelineMode): { min: number; max: number } {
    const configuredMin = this.parseDate(
      this.data?.chartConfig?.startDate || this.data?.startDate,
      false,
    );
    const configuredMax = this.parseDate(
      this.data?.chartConfig?.endDate || this.data?.endDate,
      true,
    );
    let min = configuredMin ?? Math.min(...tasks.map((task) => task.start));
    let max = configuredMax ?? Math.max(...tasks.map((task) => task.end));

    if (configuredMin === null || configuredMin === undefined) {
      min =
        mode === 'yearly'
          ? this.startOfYear(min)
          : mode === 'quarterly'
            ? this.startOfQuarter(min)
            : mode === 'monthly'
              ? this.startOfMonth(min)
              : mode === 'weekly'
                ? this.startOfWeek(min)
                : this.startOfDay(min - DAY_MS);
    }
    if (configuredMax === null || configuredMax === undefined) {
      max =
        mode === 'yearly'
          ? this.endOfYear(max)
          : mode === 'quarterly'
            ? this.endOfQuarter(max)
            : mode === 'monthly'
              ? this.endOfMonth(max)
              : mode === 'weekly'
                ? this.endOfWeek(max)
                : this.endOfDay(max + DAY_MS);
    }
    return max > min ? { min, max } : { min, max: min + 7 * DAY_MS };
  }

  private resolveViewportHeight(contentHeight: string): string {
    const configuredHeight = this.data?.height ?? this.data?.chartConfig?.height;
    if (typeof configuredHeight === 'number') return `${configuredHeight}px`;
    if (typeof configuredHeight === 'string' && configuredHeight.trim()) return configuredHeight;
    const contentHeightPx = this.toPixels(contentHeight);
    if (contentHeightPx === null) {
      return '520px';
    }
    return `${Math.max(280, Math.min(520, contentHeightPx))}px`;
  }

  private resolveContentHeight(count: number): string {
    // Reduced vertical scaling by ~50% while retaining per-row spacing.
    const rowHeight = this.useWrappedTaskLabels() ? 29 : 23;
    return `${Math.max(220, count * rowHeight + 48)}px`;
  }

  private shouldUseInternalVerticalScroll(): boolean {
    return this.data?.chartConfig?.useInternalVerticalScroll !== false;
  }

  private resolveVisibleRowWindowSize(totalRows: number): number {
    if (totalRows <= 0) {
      return 0;
    }

    const configuredVisibleRows = Number(this.data?.chartConfig?.visibleRows);
    if (Number.isFinite(configuredVisibleRows) && configuredVisibleRows > 0) {
      return Math.min(totalRows, Math.round(configuredVisibleRows));
    }

    const viewportHeightPx = this.toPixels(this.resolvedHeight) ?? 420;
    const rowHeight = this.useWrappedTaskLabels() ? 29 : 23;
    const timelineHeaderAllowance = 72;
    const availableHeight = Math.max(120, viewportHeightPx - timelineHeaderAllowance);
    const computedRows = Math.max(3, Math.floor(availableHeight / rowHeight));
    return Math.min(totalRows, computedRows);
  }

  private buildVerticalDataZoom(
    totalRows: number,
    gridTop: number,
    gridBottom: number,
  ): echarts.DataZoomComponentOption[] {
    if (!this.shouldUseInternalVerticalScroll() || totalRows <= 0) {
      return [];
    }

    const visibleRows = this.resolveVisibleRowWindowSize(totalRows);
    if (visibleRows >= totalRows) {
      return [];
    }

    const top = Math.max(0, gridTop + 2);
    const bottom = Math.max(0, gridBottom + 2);

    return [
      {
        type: 'slider',
        id: 'ganttYSlider',
        yAxisIndex: 0,
        filterMode: 'weakFilter',
        right: 8,
        top,
        bottom,
        width: 10,
        startValue: 0,
        endValue: visibleRows - 1,
        zoomLock: true,
        brushSelect: false,
        handleSize: 0,
        moveHandleSize: 0,
        showDetail: false,
      },
      {
        type: 'inside',
        id: 'ganttYInside',
        yAxisIndex: 0,
        filterMode: 'weakFilter',
        startValue: 0,
        endValue: visibleRows - 1,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
      },
    ];
  }

  private buildHorizontalDataZoom(hasUpperAxis: boolean): echarts.DataZoomComponentOption[] {
    if (this.data?.chartConfig?.enableHorizontalDragPan === false) {
      return [];
    }

    const xAxisIndex = hasUpperAxis ? [0, 1] : [0];

    return [
      {
        type: 'inside',
        id: 'ganttXInside',
        xAxisIndex,
        filterMode: 'none',
        zoomLock: true,
        start: 0,
        end: 100,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
    ];
  }

  private resolveTimelineContentWidth(
    domain: { min: number; max: number },
    mode: TimelineMode,
  ): string {
    const configuredWidth = this.data?.chartConfig?.scrollContentWidth;
    if (typeof configuredWidth === 'number' && configuredWidth > 0) {
      return `${Math.round(configuredWidth)}px`;
    }
    if (typeof configuredWidth === 'string' && configuredWidth.trim()) {
      return configuredWidth;
    }

    const spanMs = Math.max(DAY_MS, domain.max - domain.min);
    const years = spanMs / (365.25 * DAY_MS);
    const months = spanMs / (30.44 * DAY_MS);
    const weeks = spanMs / (7 * DAY_MS);
    const days = spanMs / DAY_MS;
    const basePxPerUnit =
      mode === 'yearly'
        ? 110
        : mode === 'quarterly'
          ? 16
          : mode === 'monthly'
            ? 34
            : mode === 'weekly'
              ? 18
              : 7;
    const units =
      mode === 'yearly'
        ? years
        : mode === 'quarterly'
          ? months
          : mode === 'monthly'
            ? months
            : mode === 'weekly'
              ? weeks
              : days;
    const leftPadding = this.useSideTreeMode() ? 120 : 220;
    const viewportWidth =
      this.chartHost?.nativeElement?.parentElement?.clientWidth ||
      this.chartHost?.nativeElement?.clientWidth ||
      (typeof window !== 'undefined' ? window.innerWidth : 1024);
    const timelineViewport = Math.max(320, viewportWidth - leftPadding - 100);
    const unitsInFiveYears =
      mode === 'yearly'
        ? 5
        : mode === 'quarterly'
          ? 20
          : mode === 'monthly'
            ? 60
            : mode === 'weekly'
              ? 261
              : Math.round(365.25 * 5);
    const maxPxPerUnitForFiveYearView = Math.max(2, timelineViewport / unitsInFiveYears);
    const pxPerUnit = Math.min(basePxPerUnit, maxPxPerUnitForFiveYearView);
    const timelineWidth = Math.round(units * pxPerUnit);
    const computedContentWidth = Math.min(6000, timelineWidth + leftPadding + 120);
    const minVisibleWidth = Math.max(640, viewportWidth - 2);
    return `${Math.max(minVisibleWidth, computedContentWidth)}px`;
  }

  private resolveTimelineGridPadding(position: 'top' | 'bottom', hasUpperAxis: boolean): number {
    const chartWidth = this.getChartWidth();
    const compactWidth = chartWidth < 640;

    if (position === 'top') {
      if (hasUpperAxis) {
        return compactWidth ? 70 : 58;
      }
      return compactWidth ? 42 : 34;
    }

    if (hasUpperAxis) {
      return compactWidth ? 72 : 58;
    }
    return compactWidth ? 52 : 40;
  }

  private resolveCardHeight(chartHeight: string): string {
    const chartHeightPx = this.toPixels(chartHeight);
    if (chartHeightPx === null) {
      return chartHeight;
    }

    const baseChrome = 118;
    const legendRows = this.legendItems.length ? Math.ceil(this.legendItems.length / 4) : 0;
    const legendHeight = legendRows > 0 ? legendRows * 30 + 12 : 0;
    return `${chartHeightPx + baseChrome + legendHeight}px`;
  }

  private resolveLeft(tasks: TaskItem[]): number {
    const chartWidth = this.getChartWidth();
    const maxLabelWidth =
      chartWidth < 420
        ? Math.max(112, Math.floor(chartWidth * 0.34))
        : chartWidth < 768
          ? Math.max(140, Math.floor(chartWidth * 0.38))
          : Math.max(170, Math.floor(chartWidth * 0.42));
    const configured = Number(this.data?.chartConfig?.taskLabelWidth);
    if (!Number.isNaN(configured) && configured > 0) return Math.min(configured, maxLabelWidth);
    const longest = tasks.reduce((max, task) => Math.max(max, task.rowLabel.length), 18);
    return Math.min(maxLabelWidth, Math.max(140, longest * 7 + 36));
  }

  private resolveFontScale(): number {
    if (typeof document === 'undefined') {
      return 1;
    }

    const configured = Number(this.data?.chartConfig?.fontScale);
    if (Number.isFinite(configured) && configured > 0) {
      return configured;
    }

    const cssValue = Number(
      getComputedStyle(document.documentElement).getPropertyValue('--dvm-font-scale').trim(),
    );
    return Number.isFinite(cssValue) && cssValue > 0 ? cssValue : 1;
  }

  private resolveFontSize(value: unknown, fallback: number): number {
    const configured = Number(value);
    const baseSize = Number.isFinite(configured) && configured > 0 ? configured : fallback;
    return Math.max(10, Math.round(baseSize * this.resolveFontScale()));
  }

  private resolveTaskLabelFontSize(): number {
    const baseSize = this.resolveFontSize(this.data?.chartConfig?.taskLabelFontSize, 13);
    return this.getChartWidth() < 640 ? Math.max(11, baseSize - 1) : baseSize;
  }

  private resolveTimelineLabelFontSize(emphasizeLabels: boolean): number {
    const configured = emphasizeLabels
      ? this.data?.chartConfig?.timelineLabelFontSize
      : (this.data?.chartConfig?.upperTimelineLabelFontSize ??
        this.data?.chartConfig?.timelineLabelFontSize);
    const baseSize = this.resolveFontSize(configured, emphasizeLabels ? 12 : 11);
    return this.getChartWidth() < 640 ? Math.max(10, baseSize - 1) : baseSize;
  }

  private resolveProgressLabelFontSize(): number {
    return this.resolveFontSize(this.data?.chartConfig?.progressLabelFontSize, 12);
  }

  private resolveInBarTaskLabelFontSize(): number {
    const configured =
      this.data?.chartConfig?.taskLabelInBarFontSize ?? this.data?.chartConfig?.progressLabelFontSize;
    const base = this.resolveFontSize(configured, 12);
    return this.getChartWidth() < 640 ? Math.max(10, base - 1) : base;
  }

  private resolveColorKey(
    row: TaskRecord,
    colorBy: string,
    context: { name: string; group: string; status: string; owner: string },
  ): string {
    const normalized = colorBy.trim().toLowerCase();
    const taskNameKey = this.resolveConfiguredKey('taskNameKey');
    const groupKey = this.resolveConfiguredKey('groupKey');
    const statusKey = this.resolveConfiguredKey('statusKey');
    const ownerKey = this.resolveConfiguredKey('ownerKey');

    if (
      this.matchesConfiguredKey(colorBy, taskNameKey) ||
      ['name', 'task', 'label', 'title'].includes(normalized)
    ) {
      return context.name || context.status || context.group || context.owner || 'Unassigned';
    }

    if (
      this.matchesConfiguredKey(colorBy, groupKey) ||
      ['group', 'track', 'stream', 'category'].includes(normalized)
    ) {
      return context.group || context.status || context.name || context.owner || 'Unassigned';
    }

    if (
      this.matchesConfiguredKey(colorBy, statusKey) ||
      ['status', 'phase', 'state'].includes(normalized)
    ) {
      return context.status || context.group || context.name || context.owner || 'Unassigned';
    }

    if (
      this.matchesConfiguredKey(colorBy, ownerKey) ||
      ['owner', 'team', 'assignee'].includes(normalized)
    ) {
      return context.owner || context.group || context.status || context.name || 'Unassigned';
    }

    const directValue = this.readString(row, colorBy, [colorBy]);
    return (
      directValue ||
      context.status ||
      context.group ||
      context.name ||
      context.owner ||
      'Unassigned'
    );
  }

  private resolveColorMapValue(colorMap: Record<string, string>, colorKey: string): string | null {
    if (!colorKey) {
      return null;
    }

    if (colorMap[colorKey]) {
      return colorMap[colorKey];
    }

    const normalizedKey = colorKey.trim().toLowerCase();
    const matchedKey = Object.keys(colorMap).find(
      (key) => key.trim().toLowerCase() === normalizedKey,
    );
    return matchedKey ? colorMap[matchedKey] : null;
  }

  private isInactiveByStatus(statusToken: string): boolean {
    const token = this.normalizeFilterToken(statusToken);
    if (!token) {
      return false;
    }

    const activeTokens = this.resolveConfiguredActiveTokens();
    if (activeTokens.length) {
      return !activeTokens.includes(token);
    }

    return token !== this.normalizeFilterToken('Active/Approved');
  }

  private resolveConfiguredActiveTokens(): string[] {
    const raw =
      this.data?.chartConfig?.activeValues ||
      this.data?.chartConfig?.activeStatuses ||
      this.data?.activeValues ||
      this.data?.activeStatuses;

    if (!Array.isArray(raw)) {
      return [];
    }

    const normalized = raw
      .map((value: unknown) => this.normalizeFilterToken(value))
      .filter((token: string) => !!token);
    return Array.from(new Set(normalized));
  }

  private matchesConfiguredKey(colorBy: string, configuredKey: unknown): boolean {
    return (
      typeof configuredKey === 'string' &&
      configuredKey.trim().toLowerCase() === colorBy.trim().toLowerCase()
    );
  }

  private useWrappedTaskLabels(): boolean {
    return this.data?.chartConfig?.wrapTaskLabels !== false;
  }

  private formatTaskAxisLabel(value: string, maxWidth: number, fontSize: number): string {
    const label = String(value || '').trim();
    if (!label || !this.useWrappedTaskLabels()) {
      return label;
    }

    // Factor 0.5 gives a generous chars-per-line estimate so more text fits before breaking
    const maxCharsPerLine = Math.max(10, Math.floor(maxWidth / Math.max(fontSize * 0.5, 6)));
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (label.includes(separator)) {
      // Separator format: group name on line 1, task name wrapped to fill line 2
      // Strictly 2 lines total to avoid bleeding into adjacent rows
      const [group, ...rest] = label.split(separator);
      const groupLine = this.truncateText(group.trim(), maxCharsPerLine);
      const taskPart = rest.join(separator).trim();
      // wrapText with maxLines=1 gives one wrapped/truncated line for the task
      const taskLine = this.wrapText(taskPart, maxCharsPerLine, 1);
      return taskLine ? `${groupLine}\n${taskLine}` : groupLine;
    }

    // Plain labels: wrap to strictly 2 lines to match row height budget
    return this.wrapText(label, maxCharsPerLine, 2);
  }

  private resolveResponsiveUpperTimelineMode(upperMode: TimelineMode | null): TimelineMode | null {
    if (!upperMode) {
      return null;
    }

    if (this.data?.chartConfig?.alwaysShowUpperTimeline === true) {
      return upperMode;
    }

    const hideBelow = Number(this.data?.chartConfig?.hideUpperTimelineBelow || 640);
    return this.getChartWidth() < hideBelow ? null : upperMode;
  }

  private resolveTimelineSplitNumber(
    mode: TimelineMode,
    emphasizeLabels: boolean,
    chartWidth: number,
  ): number | undefined {
    if (mode === 'yearly') {
      return chartWidth < 640 ? 4 : undefined;
    }

    if (chartWidth < 420) {
      return emphasizeLabels ? 3 : 4;
    }

    if (chartWidth < 640) {
      return emphasizeLabels ? 4 : 5;
    }

    if (chartWidth < 900) {
      return emphasizeLabels ? 5 : 7;
    }

    return undefined;
  }

  private getTimelineLabel(mode: TimelineMode): string {
    return mode === 'weekly'
      ? 'Weekly Timeline'
      : mode === 'monthly'
        ? 'Monthly Timeline'
        : mode === 'quarterly'
          ? 'Quarterly Timeline'
          : mode === 'yearly'
            ? 'Yearly Timeline'
            : 'Date Timeline';
  }

  private tick(
    value: number,
    mode: TimelineMode,
    compact = false,
    shiftQuarterLabelToPrevious = false,
  ): string {
    const date = new Date(value);
    if (mode === 'yearly') {
      return new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date);
    }
    if (mode === 'quarterly') {
      const displayDate = shiftQuarterLabelToPrevious
        ? new Date(date.getFullYear(), date.getMonth() - 3, 1)
        : date;
      const quarter = Math.floor(displayDate.getMonth() / 3) + 1;
      return compact ? `Q${quarter}` : `Q${quarter} ${displayDate.getFullYear()}`;
    }
    if (mode === 'monthly') {
      return new Intl.DateTimeFormat(
        'en-US',
        compact ? { month: 'short' } : { month: 'short', year: 'numeric' },
      ).format(date);
    }
    if (mode === 'weekly') {
      const week = this.isoWeek(date);
      return compact ? `W${week.week}` : `W${week.week} ${week.year}`;
    }
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  }

  private minInterval(mode: TimelineMode): number {
    const step = Math.max(1, Number(this.data?.chartConfig?.timelineStep || 1));
    return mode === 'yearly'
      ? 365 * DAY_MS * step
      : mode === 'quarterly'
        ? 90 * DAY_MS * step
        : mode === 'monthly'
          ? 28 * DAY_MS * step
          : mode === 'weekly'
            ? 7 * DAY_MS * step
            : DAY_MS * step;
  }

  private buildRowLabel(name: string, group: string, engineModel: string): string {
    if (this.useSideTreeMode()) {
      return engineModel ? `${engineModel}: ${name}` : name;
    }

    const labelMode = String(this.data?.chartConfig?.rowLabelMode || 'group-task').toLowerCase();
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (labelMode === 'task' || labelMode === 'task-only') {
      return name;
    }
    if (labelMode === 'group') {
      return group || name;
    }
    return group ? `${group}${separator}${name}` : name;
  }

  private buildSelectionKey(name: string, group: string, engineModel: string): string {
    if (this.useSideTreeMode()) {
      return engineModel ? `${engineModel}: ${name}` : name;
    }

    const selectionMode = String(
      this.data?.chartConfig?.selectionKeyMode ||
        this.data?.chartConfig?.rowLabelMode ||
        'group-task',
    ).toLowerCase();
    const separator = String(this.data?.chartConfig?.labelSeparator || ' • ');

    if (selectionMode === 'task' || selectionMode === 'task-only') {
      return name;
    }
    if (selectionMode === 'group') {
      return group || name;
    }
    return group ? `${group}${separator}${name}` : name;
  }

  private uniquify(values: string[]): string[] {
    const counts = new Map<string, number>();
    const seen = new Map<string, number>();

    values.forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));

    return values.map((value) => {
      if ((counts.get(value) || 0) === 1) {
        return value;
      }

      const next = (seen.get(value) || 0) + 1;
      seen.set(value, next);
      return `${value} (${next})`;
    });
  }

  private resolveLegendItems(tasks: TaskItem[]): LegendItem[] {
    if (this.data?.chartConfig?.showLegend === false) {
      return [];
    }

    const configuredLegendItems = this.resolveConfiguredLegendItems();
    if (configuredLegendItems.length) {
      return configuredLegendItems.map((item) => ({ label: item.label, color: item.color }));
    }

    const items = new Map<string, LegendItem>();
    for (const task of tasks) {
      const label = task.colorKey || task.status || task.group || task.name;
      if (!items.has(label)) {
        items.set(label, { label, color: task.color });
      }
    }

    return Array.from(items.values());
  }

  protected onLegendClick(label: string): void {
    if (this.selectedLegendLabels.includes(label)) {
      if (this.selectedLegendLabels.length === 1) {
        return;
      }
      this.selectedLegendLabels = this.selectedLegendLabels.filter((item) => item !== label);
    } else {
      this.selectedLegendLabels = [...this.selectedLegendLabels, label];
    }
    this.applyLegendSelectionToExternalFilters();
    this.selectedTaskKey = null;
    this.renderVisibleTasks();
  }

  protected isLegendActive(label: string): boolean {
    return this.selectedLegendLabels.includes(label);
  }

  protected onLineStyleLegendClick(style: LineStyleLegendKey): void {
    if (this.selectedLineStyleLegendKeys.includes(style)) {
      if (this.selectedLineStyleLegendKeys.length === 1) {
        return;
      }
      this.selectedLineStyleLegendKeys = this.selectedLineStyleLegendKeys.filter(
        (item) => item !== style,
      );
    } else {
      this.selectedLineStyleLegendKeys = [...this.selectedLineStyleLegendKeys, style];
    }

    this.applyLegendSelectionToExternalFilters();
    this.selectedTaskKey = null;
    this.renderVisibleTasks();
  }

  protected isLineStyleLegendActive(style: LineStyleLegendKey): boolean {
    return (
      this.selectedLineStyleLegendKeys.length === 0 ||
      this.selectedLineStyleLegendKeys.includes(style)
    );
  }

  private applyLegendSelectionToExternalFilters(): void {
    const statusFilterKey = this.resolveConfiguredKey('statusKey') || 'status_cleaned';
    const baseStatuses = this.extractCurrentStatusFilterValues(statusFilterKey);
    const allKnownStatuses = this.resolveAllKnownStatuses();
    let selectedStatuses = this.resolveLegendDrivenStatuses();

    if (selectedStatuses.length && baseStatuses.length) {
      const baseStatusTokens = new Set(baseStatuses.map((status) => this.normalizeFilterToken(status)));
      selectedStatuses = selectedStatuses.filter((status) =>
        baseStatusTokens.has(this.normalizeFilterToken(status)),
      );
    }

    if (!this.hasAnyLegendRestriction()) {
      selectedStatuses = baseStatuses.length ? baseStatuses : allKnownStatuses;
    }

    if (selectedStatuses.length) {
      this.filterState.updateFilterSelection(
        { [statusFilterKey]: selectedStatuses },
        this.data?.widgetId,
      );
      this.globalFilterState.updateFilterSelection(
        statusFilterKey,
        selectedStatuses,
        false,
        this.data?.widgetId,
      );
      return;
    }

    this.filterState.updateFilterSelection(
      { [statusFilterKey]: [] },
      this.data?.widgetId,
    );
    this.globalFilterState.resetFilterSelection(statusFilterKey, false);
  }

  private extractCurrentStatusFilterValues(statusFilterKey: string): string[] {
    const mergedFilters = this.mergeActiveFilters();
    const normalizedFilters = this.remapFilterKeys(mergedFilters || {});
    const rawValue = normalizedFilters[statusFilterKey];
    const values = Array.isArray(rawValue)
      ? rawValue
          .flat(Infinity)
          .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
          .filter((value) => !!value)
      : [];

    return Array.from(new Set(values)).filter((value) => value.toLowerCase() !== 'all');
  }

  private resolveLegendDrivenStatuses(): string[] {
    const allStatuses = this.resolveAllKnownStatuses();
    if (!allStatuses.length) {
      return [];
    }

    let selectedStatuses: string[] | null = null;

    const strategicStatuses = this.resolveStrategicLegendStatuses();
    if (strategicStatuses.length) {
      selectedStatuses = strategicStatuses;
    }

    const activityStatuses = this.resolveActivityLegendStatuses(allStatuses);
    if (activityStatuses.length) {
      selectedStatuses = selectedStatuses
        ? selectedStatuses.filter((status) => activityStatuses.includes(status))
        : activityStatuses;
    }

    return this.alignStatusesToKnownCasing(selectedStatuses ?? [], allStatuses);
  }

  private resolveStrategicLegendStatuses(): string[] {
    if (!this.hasStrategicLegendRestriction()) {
      return [];
    }

    const configured = this.resolveConfiguredLegendItems();
    const statuses = this.selectedLegendLabels.flatMap((selectedLabel) => {
      const item = configured.find((entry) => entry.label === selectedLabel);
      if (!item?.keys?.length) {
        return [];
      }
      return item.keys.map((key) => String(key || '').trim()).filter((key) => !!key);
    });

    return Array.from(new Set(statuses));
  }

  private resolveActivityLegendStatuses(allStatuses: string[]): string[] {
    if (!this.hasActivityLegendRestriction()) {
      return [];
    }

    const activeTokens = this.resolveConfiguredActiveTokens();
    const normalizedActive = new Set(activeTokens);

    const activeStatuses = allStatuses.filter((status) =>
      normalizedActive.has(this.normalizeFilterToken(status)),
    );
    const inactiveStatuses = allStatuses.filter(
      (status) => !normalizedActive.has(this.normalizeFilterToken(status)),
    );

    const statuses: string[] = [];
    if (this.selectedLineStyleLegendKeys.includes('solid')) {
      statuses.push(...activeStatuses);
    }
    if (this.selectedLineStyleLegendKeys.includes('dotted')) {
      statuses.push(...inactiveStatuses);
    }

    return Array.from(new Set(statuses));
  }

  private hasStrategicLegendRestriction(): boolean {
    return this.legendItems.length > 0 && this.selectedLegendLabels.length < this.legendItems.length;
  }

  private hasActivityLegendRestriction(): boolean {
    const totalActivityStyles = this.data?.chartConfig?.showLineStyleLegend ? 2 : 0;
    return totalActivityStyles > 0 && this.selectedLineStyleLegendKeys.length < totalActivityStyles;
  }

  private hasAnyLegendRestriction(): boolean {
    return this.hasStrategicLegendRestriction() || this.hasActivityLegendRestriction();
  }

  private resolveAllKnownStatuses(): string[] {
    const statuses = this.allTasks
      .map((task) => String(task.status || '').trim())
      .filter((status) => !!status);
    return Array.from(new Set(statuses));
  }

  private alignStatusesToKnownCasing(statuses: string[], allKnownStatuses: string[]): string[] {
    if (!statuses.length || !allKnownStatuses.length) {
      return statuses;
    }

    const knownByToken = new Map<string, string>();
    for (const known of allKnownStatuses) {
      const token = this.normalizeFilterToken(known);
      if (token && !knownByToken.has(token)) {
        knownByToken.set(token, known);
      }
    }

    const resolved: string[] = [];
    const seenTokens = new Set<string>();
    for (const status of statuses) {
      const token = this.normalizeFilterToken(status);
      if (!token || seenTokens.has(token)) {
        continue;
      }
      seenTokens.add(token);
      resolved.push(knownByToken.get(token) || status);
    }

    return resolved;
  }

  protected collapseAllTreeGroups(): void {
    for (const segment of this.sideTree) {
      this.collapsedSegmentKeys.add(segment.key);
      for (const model of segment.models) {
        this.collapsedModelKeys.add(model.key);
      }
    }
    this.activeSegmentKey = null;
    this.activeModelKey = null;
    this.selectedSegmentKeys.clear();
    this.selectedTaskKey = null;
    this.renderVisibleTasks();
  }

  protected expandAllTreeGroups(): void {
    this.collapsedSegmentKeys.clear();
    this.collapsedModelKeys.clear();
    this.activeSegmentKey = null;
    this.activeModelKey = null;
    this.selectedSegmentKeys.clear();
    this.selectedTaskKey = null;
    this.renderVisibleTasks();
  }

  protected toggleSegmentSelection(segment: SideTreeSegment): void {
    this.selectedTaskKey = null;
    if (this.isSideTreeMultiSelectEnabled()) {
      if (this.selectedSegmentKeys.has(segment.key)) {
        this.selectedSegmentKeys.delete(segment.key);
      } else {
        this.selectedSegmentKeys.add(segment.key);
        this.collapsedSegmentKeys.delete(segment.key);
      }
    } else {
      if (this.selectedSegmentKeys.size === 1 && this.selectedSegmentKeys.has(segment.key)) {
        this.selectedSegmentKeys.clear();
      } else {
        this.selectedSegmentKeys.clear();
        this.selectedSegmentKeys.add(segment.key);
        this.collapsedSegmentKeys.delete(segment.key);
      }
    }

    this.syncActiveTreeSelection();
    this.renderVisibleTasks();
  }

  protected toggleSegmentCollapse(segment: SideTreeSegment): void {
    this.selectedTaskKey = null;
    if (this.collapsedSegmentKeys.has(segment.key)) {
      this.collapsedSegmentKeys.delete(segment.key);
    } else {
      this.collapsedSegmentKeys.add(segment.key);
      this.selectedSegmentKeys.delete(segment.key);
    }

    this.syncActiveTreeSelection();
    this.renderVisibleTasks();
  }

  protected toggleModel(segment: SideTreeSegment, model: SideTreeModel): void {
    this.selectedTaskKey = null;
    this.selectedSegmentKeys.clear();
    if (this.collapsedSegmentKeys.has(segment.key)) {
      this.collapsedSegmentKeys.delete(segment.key);
    }

    if (this.collapsedModelKeys.has(model.key)) {
      this.collapsedModelKeys.delete(model.key);
      this.activeSegmentKey = segment.key;
      this.activeModelKey = model.key;
    } else if (this.activeModelKey === model.key) {
      this.collapsedModelKeys.add(model.key);
      this.activeSegmentKey = null;
      this.activeModelKey = null;
    } else {
      this.activeSegmentKey = segment.key;
      this.activeModelKey = model.key;
    }
    this.renderVisibleTasks();
  }

  protected isSegmentFocused(segment: SideTreeSegment): boolean {
    return this.selectedSegmentKeys.has(segment.key);
  }

  protected isModelFocused(model: SideTreeModel): boolean {
    return this.activeModelKey === model.key;
  }

  private resetInteractiveState(): void {
    this.selectedLegendLabels = [];
    this.selectedLineStyleLegendKeys = [];
    this.collapsedSegmentKeys.clear();
    this.collapsedModelKeys.clear();
    this.selectedSegmentKeys.clear();
    this.activeSegmentKey = null;
    this.activeModelKey = null;
    this.selectedTaskKey = null;
    this.isSideTreeCollapsed = false;

    if (this.allTasks.length && this.currentDomain) {
      this.renderVisibleTasks();
    }
  }

  private syncActiveTreeSelection(): void {
    const [firstSelectedSegmentKey] = Array.from(this.selectedSegmentKeys);
    this.activeSegmentKey = firstSelectedSegmentKey || null;
    this.activeModelKey = null;
  }

  private isSideTreeMultiSelectEnabled(): boolean {
    return (
      this.data?.chartConfig?.sideTreeMultiSelect !== false &&
      this.data?.sideTreeMultiSelect !== false
    );
  }

  /**
   * When a specific segment filter is active (not "All"), collapse every
   * segment in the side-tree that does not match the selected values so
   * only the chosen segment's tree is expanded.
   */
  private autoCollapseSegmentsByFilter(filters?: Record<string, unknown>): void {
    if (!filters || !this.useSideTreeMode() || !this.allTasks.length) {
      return;
    }

    const segmentKey =
      this.data?.chartConfig?.segmentKey ||
      this.data?.segmentKey ||
      'cleaned_vertical';
    const normalizedFilters = this.remapFilterKeys(filters);
    const selectedValues = this.normalizeFilterValues(normalizedFilters[segmentKey]);

    // If nothing is selected or "all" is chosen, keep everything expanded.
    if (!selectedValues.length || selectedValues.includes('all')) {
      return;
    }

    // Build a set of matching tree-keys from the selected filter values.
    const matchingTreeKeys = new Set(
      selectedValues.map((value) => this.toTreeKey(value)),
    );
    const commonSegmentKey = this.toTreeKey(COMMON_SIDE_TREE_SEGMENT_KEY_LABEL);

    // Collect all unique segment tree-keys from the current tasks.
    const allSegmentKeys = new Set<string>();
    for (const task of this.allTasks) {
      const tokens = this.resolveSideTreeSegmentTokens(task);
      for (const token of tokens) {
        allSegmentKeys.add(this.toTreeKey(token));
      }
    }

    // Collapse every segment that is NOT in the selected filter values.
    for (const key of allSegmentKeys) {
      if (key === commonSegmentKey) {
        continue;
      }
      if (!matchingTreeKeys.has(key)) {
        this.collapsedSegmentKeys.add(key);
      }
    }
  }

  private renderVisibleTasks(): void {
    const legendFilteredTasks = this.selectedLegendLabels.length
      ? this.allTasks.filter((task) => this.selectedLegendLabels.some((label) => this.matchesLegend(task, label)))
      : this.allTasks;
    const lineStyleFilteredTasks = this.selectedLineStyleLegendKeys.length
      ? legendFilteredTasks.filter((task) =>
          this.selectedLineStyleLegendKeys.some((style) => this.matchesLineStyleLegend(task, style)))
      : legendFilteredTasks;
    this.rebuildSideTree(lineStyleFilteredTasks);
    const treeVisibleTasks = this.showSideTree
      ? lineStyleFilteredTasks.filter((task) => this.isVisibleInTree(task))
      : lineStyleFilteredTasks;
    const hasExplicitTreeSelection =
      this.selectedTaskKey !== null ||
      this.selectedSegmentKeys.size > 0 ||
      this.activeSegmentKey !== null ||
      this.activeModelKey !== null;
    const allSegmentsCollapsed =
      this.showSideTree &&
      this.sideTree.length > 0 &&
      this.sideTree.every((segment) => this.collapsedSegmentKeys.has(segment.key));
    const effectiveTreeVisibleTasks = allSegmentsCollapsed ? lineStyleFilteredTasks : treeVisibleTasks;
    const visibleTasks = hasExplicitTreeSelection
      ? this.resolveFocusedTasks(effectiveTreeVisibleTasks)
      : effectiveTreeVisibleTasks;

    if (!visibleTasks.length || !this.currentDomain) {
      // Preserve chart footprint when filters yield no rows so layout does not collapse.
      const stableViewportHeight = this.resolveViewportHeight(this.resolvedHeight);
      this.resolvedHeight = stableViewportHeight;
      this.resolvedChartContentHeight = stableViewportHeight;
      this.resolvedCardHeight = this.resolveCardHeight(stableViewportHeight);
      this.hasError = true;
      this.errorMessage = this.selectedLegendLabels.length || this.selectedLineStyleLegendKeys.length
        ? 'No roadmap items match the selected legend.'
        : 'No roadmap items match the selected filters.';
      this.taskCount = 0;
      this.allCategories = [];
      this.myChart?.clear();
      this.myChart?.hideLoading();
      return;
    }

    this.hasError = false;
    this.errorMessage = '';
    this.taskCount = visibleTasks.length;
    this.allCategories = visibleTasks.map((task) => task.selectionKey);
    const computedContentHeight = this.resolveContentHeight(visibleTasks.length);
    this.resolvedHeight = this.resolveViewportHeight(computedContentHeight);
    this.resolvedChartBodyHeight = this.resolveChartBodyHeight(this.resolvedHeight);
    this.resolvedChartContentHeight = this.shouldUseInternalVerticalScroll()
      ? this.resolvedHeight
      : this.ensureContentHeightAtLeastViewport(computedContentHeight, this.resolvedHeight);
    const renderDomain = this.resolveRenderDomain(visibleTasks);
    this.lastRenderDomain = renderDomain;
    this.resolvedChartContentWidth = this.resolveTimelineContentWidth(renderDomain, this.currentMode);
    this.resolvedCardHeight = this.resolveCardHeight(this.resolvedHeight);
    this.chartHost.nativeElement.style.height = this.resolvedChartContentHeight;
    this.chartHost.nativeElement.style.width = this.resolvedChartContentWidth;
    this.labelDebugRenderCount = 0;

    this.myChart.setOption(
      this.buildOption(visibleTasks, this.currentMode, renderDomain),
      true,
    );
    this.myChart.hideLoading();
    this.setProductRoadmapLoadingForInitialFetch(false);
    setTimeout(() => {
      this.myChart?.resize();
      this.updateTimelineHeaderMarkersFromChart(renderDomain);
      this.scrollFocusIntoView(visibleTasks, renderDomain);
      this.syncTimelineHeaderScroll();
    }, 0);
  }

  public onChartShellScroll(): void {
    this.syncTimelineHeaderScroll();
  }

  private syncTimelineHeaderScroll(): void {
    const shell = this.chartShell?.nativeElement;
    if (!shell) {
      return;
    }
    this.timelineHeaderScrollLeft = shell.scrollLeft;
  }

  private shouldUseExternalTimelineHeader(): boolean {
    return this.data?.chartConfig?.useExternalTimelineHeader !== false;
  }

  private resolveTimelineHeaderMarkers(
    domain: { min: number; max: number },
    plotLeft: number,
  ): TimelineHeaderMarker[] {
    if (!this.shouldUseExternalTimelineHeader()) {
      return [];
    }

    const contentWidth = this.toPixels(this.resolvedChartContentWidth) ?? 1200;
    const rightPadding = 24;
    const plotWidth = Math.max(1, contentWidth - plotLeft - rightPadding);
    const span = Math.max(DAY_MS, domain.max - domain.min);
    const markers: TimelineHeaderMarker[] = [];

    let yearCursor = this.startOfYear(domain.min);
    while (yearCursor <= domain.max) {
      const ratio = Math.max(0, Math.min(1, (yearCursor - domain.min) / span));
      markers.push({
        left: Math.round(plotLeft + ratio * plotWidth),
        label: new Date(yearCursor).getFullYear().toString(),
      });
      yearCursor = this.addMonths(yearCursor, 12);
    }

    return markers;
  }

  private updateTimelineHeaderMarkersFromChart(
    domain: { min: number; max: number } | null = this.lastRenderDomain,
  ): void {
    if (!domain || !this.shouldUseExternalTimelineHeader() || !this.myChart) {
      return;
    }

    const minPixel = Number(this.myChart.convertToPixel({ xAxisIndex: 0 }, domain.min));
    const maxPixel = Number(this.myChart.convertToPixel({ xAxisIndex: 0 }, domain.max));
    if (!Number.isFinite(minPixel) || !Number.isFinite(maxPixel) || maxPixel <= minPixel) {
      return;
    }

    const markers: TimelineHeaderMarker[] = [];
    let yearCursor = this.startOfYear(domain.min);
    while (yearCursor <= domain.max) {
      const left = Number(this.myChart.convertToPixel({ xAxisIndex: 0 }, yearCursor));
      if (Number.isFinite(left) && left >= minPixel - 0.5 && left <= maxPixel + 0.5) {
        markers.push({
          left: Math.round(left),
          label: new Date(yearCursor).getFullYear().toString(),
        });
      }
      yearCursor = this.addMonths(yearCursor, 12);
    }

    this.timelineHeaderMarkers = markers;
  }

  private resolveChartBodyHeight(containerHeight: string): string {
    const heightPx = this.toPixels(containerHeight);
    if (heightPx === null) {
      return containerHeight;
    }
    return `${Math.max(160, heightPx - 24)}px`;
  }

  private setProductRoadmapLoadingForInitialFetch(isLoading: boolean): void {
    if (!this.isProductRoadmapPrimaryGantt()) {
      return;
    }

    if (!this.hasCompletedInitialLoad) {
      this.filterState.setProductRoadmapLoading(isLoading);
      if (!isLoading) {
        this.hasCompletedInitialLoad = true;
      }
    }
  }

  private isProductRoadmapPrimaryGantt(): boolean {
    return this.data?.widgetId === 'productRoadmapGantt';
  }

  private ensureContentHeightAtLeastViewport(
    contentHeight: string,
    viewportHeight: string,
  ): string {
    const contentPx = this.toPixels(contentHeight);
    const viewportPx = this.toPixels(viewportHeight);
    if (contentPx === null || viewportPx === null) {
      return contentHeight;
    }
    return `${Math.max(contentPx, viewportPx)}px`;
  }

  private useSideTreeMode(): boolean {
    return this.data?.chartConfig?.showSideTree === true || this.data?.showSideTree === true;
  }

  private getSegmentSortOrder(): string[] {
    const configured = this.data?.chartConfig?.segmentSortOrder ?? this.data?.segmentSortOrder;
    if (Array.isArray(configured) && configured.length) {
      const normalizedConfigured = configured
        .map((value) => String(value || '').trim())
        .filter((value) => !!value);
      const hasCrossPlatform = normalizedConfigured.some(
        (value) => this.normalizeSegmentSortLabel(value) === 'crossplatform',
      );
      return hasCrossPlatform
        ? normalizedConfigured
        : [...normalizedConfigured, 'Cross Segment'];
    }

    return DEFAULT_SEGMENT_SORT_ORDER;
  }

  private normalizeSegmentSortLabel(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }

    // Multi-segment assignments are treated as Cross Platform and should sort last.
    if (this.shouldSplitTreeCommaValues()) {
      const segmentTokens = raw
        .split(/[;,|]+|\s\/\s|\//g)
        .map((token) => token.trim())
        .filter((token) => !!token);
      if (segmentTokens.length > 1) {
        return 'crossplatform';
      }
    }

    const normalized = raw.toLowerCase().replace(/[^\w]+/g, '');

    if (!normalized) {
      return '';
    }

    if (
      normalized === 'oilandgas' ||
      normalized === 'oilgas' ||
      normalized === 'oandg' ||
      normalized === 'og'
    ) {
      return 'og';
    }

    if (normalized === 'defence') {
      return 'defense';
    }
    if (normalized === 'common' || normalized === 'crossplatform' || normalized === 'crosssegment') {
      return 'crossplatform';
    }

    return normalized;
  }

  private getSegmentSortRank(value: string): number {
    const normalizedValue = this.normalizeSegmentSortLabel(value);
    if (!normalizedValue) {
      return Number.MAX_SAFE_INTEGER;
    }

    const order = this.getSegmentSortOrder();
    const rank = order.findIndex(
      (item) => this.normalizeSegmentSortLabel(item) === normalizedValue,
    );
    return rank >= 0 ? rank : Number.MAX_SAFE_INTEGER;
  }

  private compareTasksForDisplay(a: TaskItem, b: TaskItem): number {
    const segmentRankDiff = this.getSegmentSortRank(a.segment) - this.getSegmentSortRank(b.segment);
    if (segmentRankDiff !== 0) {
      return segmentRankDiff;
    }

    return a.name.localeCompare(b.name) || a.rowLabel.localeCompare(b.rowLabel) || a.start - b.start || a.end - b.end;
  }

  private rebuildSideTree(tasks: TaskItem[]): void {
    if (!this.useSideTreeMode()) {
      this.sideTree = [];
      this.showSideTree = false;
      this.isSideTreeCollapsed = false;
      return;
    }

    const segmentMap = new Map<
      string,
      { label: string; taskKeys: Set<string>; models: Map<string, { taskKeys: Set<string>; sampleTask: TaskItem }> }
    >();

    for (const task of tasks) {
      const segmentTokens = this.resolveSideTreeSegmentTokens(task);
      const modelTokens = this.resolveSideTreeModelTokens(task);
      const taskKey = task.selectionKey || task.name || `${task.start}-${task.end}`;

      for (const segmentLabel of segmentTokens) {
        const segmentKey = this.toTreeKey(segmentLabel);
        if (!segmentMap.has(segmentKey)) {
          segmentMap.set(segmentKey, {
            label: segmentLabel,
            taskKeys: new Set<string>(),
            models: new Map<string, { taskKeys: Set<string>; sampleTask: TaskItem }>(),
          });
        }

        const segmentEntry = segmentMap.get(segmentKey)!;
        segmentEntry.taskKeys.add(taskKey);
        for (const modelLabel of modelTokens) {
          const modelKey = this.toModelTreeKey(segmentKey, modelLabel);
          if (!segmentEntry.models.has(modelKey)) {
            segmentEntry.models.set(modelKey, { taskKeys: new Set<string>(), sampleTask: task });
          }
          segmentEntry.models.get(modelKey)!.taskKeys.add(taskKey);
        }
      }
    }

    this.sideTree = Array.from(segmentMap.entries())
      .map(([segmentKey, segmentEntry]) => {
        const models = Array.from(segmentEntry.models.entries())
          .map(([modelKey, modelEntry]) => ({
            key: modelKey,
            label:
              this.resolveSideTreeModelTokens(modelEntry.sampleTask).find(
                (token) => this.toModelTreeKey(segmentKey, token) === modelKey,
              ) || 'Unspecified',
            count: modelEntry.taskKeys.size,
            collapsed: this.collapsedModelKeys.has(modelKey),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return {
          key: segmentKey,
          label: this.resolveSideTreeSegmentDisplayLabel(segmentEntry.label),
          count: segmentEntry.taskKeys.size,
          collapsed: this.collapsedSegmentKeys.has(segmentKey),
          models,
        };
      })
      .sort((a, b) => {
        const commonSegmentKey = this.toTreeKey(COMMON_SIDE_TREE_SEGMENT_KEY_LABEL);
        const aIsCommon = a.key === commonSegmentKey;
        const bIsCommon = b.key === commonSegmentKey;
        if (aIsCommon && !bIsCommon) {
          return 1;
        }
        if (!aIsCommon && bIsCommon) {
          return -1;
        }
        const segmentRankDiff = this.getSegmentSortRank(a.label) - this.getSegmentSortRank(b.label);
        if (segmentRankDiff !== 0) {
          return segmentRankDiff;
        }
        return a.label.localeCompare(b.label);
      });

    this.showSideTree = this.sideTree.length > 0;
    if (!this.showSideTree) {
      this.isSideTreeCollapsed = false;
    }
  }

  private isVisibleInTree(task: TaskItem): boolean {
    const segmentTokens = this.resolveSideTreeSegmentTokens(task);
    const modelTokens = this.resolveSideTreeModelTokens(task);

    for (const segmentLabel of segmentTokens) {
      const segmentKey = this.toTreeKey(segmentLabel);
      if (this.collapsedSegmentKeys.has(segmentKey)) {
        continue;
      }

      for (const modelLabel of modelTokens) {
        const modelKey = this.toModelTreeKey(segmentKey, modelLabel);
        if (!this.collapsedModelKeys.has(modelKey)) {
          return true;
        }
      }
    }

    return false;
  }

  private resolveSideTreeModelTokens(task: TaskItem | undefined): string[] {
    if (!task) {
      return ['Unspecified'];
    }

    const configuredChildKey = this.resolveConfiguredKey('sideTreeChildKey');
    if (configuredChildKey) {
      const configuredValue = this.readString(
        task as unknown as TaskRecord,
        configuredChildKey,
        [],
      );
      if (configuredValue) {
        return this.resolveTreeTokens(configuredValue, 'Unspecified');
      }
    }

    const childLabelMode = String(
      this.data?.chartConfig?.sideTreeChildLabelMode || this.data?.sideTreeChildLabelMode || 'task',
    )
      .trim()
      .toLowerCase();
    const engineModel = (task.engineModel || '').trim();
    const taskName = (task.name || '').trim();

    if (
      childLabelMode === 'engine-task' ||
      childLabelMode === 'engine-model-task' ||
      childLabelMode === 'engine-title'
    ) {
      const titleLabel = taskName || 'Unspecified';
      const modelTokens = this.resolveTreeTokens(engineModel || 'Unspecified', 'Unspecified');
      return modelTokens.map((modelToken) => `${modelToken}: ${titleLabel}`);
    }

    if (childLabelMode === 'engine' || childLabelMode === 'engine-model') {
      return this.resolveTreeTokens(engineModel || 'Unspecified', 'Unspecified');
    }

    if (taskName) {
      return this.resolveTreeTokens(taskName, 'Unspecified');
    }

    return this.resolveTreeTokens(engineModel || 'Unspecified', 'Unspecified');
  }

  private resolveTreeTokens(rawValue: string, fallback: string): string[] {
    const normalized = String(rawValue || '').trim();
    if (!normalized) {
      return [fallback];
    }

    if (!this.shouldSplitTreeCommaValues()) {
      return [normalized];
    }

    const tokens = normalized
      .split(',')
      .map((token) => token.trim())
      .filter((token) => !!token);
    const uniqueTokens = Array.from(new Set(tokens));
    return uniqueTokens.length ? uniqueTokens : [fallback];
  }

  private shouldSplitTreeCommaValues(): boolean {
    return (
      this.data?.chartConfig?.sideTreeSplitCommaValues === true ||
      this.data?.sideTreeSplitCommaValues === true
    );
  }

  protected resolveSideTreeMinWidth(): string {
    const configured = Number(this.data?.chartConfig?.sideTreeMinWidth);
    if (Number.isFinite(configured) && configured > 0) {
      return `${configured}px`;
    }
    return '280px';
  }

  protected resolveSideTreeMaxWidth(): string {
    const min = Number(this.data?.chartConfig?.sideTreeMinWidth);
    const configured = Number(this.data?.chartConfig?.sideTreeMaxWidth);
    if (Number.isFinite(configured) && configured > 0) {
      if (Number.isFinite(min) && min > 0 && configured < min) {
        return `${min}px`;
      }
      return `${configured}px`;
    }
    return '420px';
  }

  private toTreeKey(value: string): string {
    return String(value || 'other')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
  }

  private toModelTreeKey(segmentKey: string, modelLabel: string): string {
    return `${segmentKey}::${this.toTreeKey(modelLabel || 'unspecified')}`;
  }

  private resolveSideTreeSegmentTokens(task: TaskItem | undefined): string[] {
    if (!task) {
      return ['Other'];
    }

    const segmentTokens = this.resolveTreeTokens(task.segment || task.group || 'Other', 'Other');
    if (segmentTokens.length > 1) {
      return [COMMON_SIDE_TREE_SEGMENT_KEY_LABEL];
    }

    return segmentTokens;
  }

  private resolveSideTreeSegmentDisplayLabel(label: string): string {
    if (this.toTreeKey(label) === this.toTreeKey(COMMON_SIDE_TREE_SEGMENT_KEY_LABEL)) {
      return COMMON_SIDE_TREE_SEGMENT_DISPLAY_LABEL;
    }
    return label;
  }

  private isTaskInActiveTreeFocus(task: TaskItem): boolean {
    if (!this.activeSegmentKey && !this.activeModelKey && !this.selectedSegmentKeys.size) {
      return true;
    }

    const segmentTokens = this.resolveSideTreeSegmentTokens(task);
    for (const segmentLabel of segmentTokens) {
      const segmentKey = this.toTreeKey(segmentLabel);
      if (this.selectedSegmentKeys.has(segmentKey)) {
        return true;
      }
      if (this.activeModelKey) {
        const modelTokens = this.resolveSideTreeModelTokens(task);
        for (const modelLabel of modelTokens) {
          if (this.toModelTreeKey(segmentKey, modelLabel) === this.activeModelKey) {
            return true;
          }
        }
      } else if (segmentKey === this.activeSegmentKey) {
        return true;
      }
    }

    return false;
  }

  private resolveFocusedTasks(visibleTasks: TaskItem[]): TaskItem[] {
    if (!visibleTasks.length) {
      return [];
    }

    if (this.selectedTaskKey) {
      const selected = visibleTasks.find((task) => task.selectionKey === this.selectedTaskKey);
      if (selected) {
        return [selected];
      }
      this.selectedTaskKey = null;
    }

    if (this.selectedSegmentKeys.size) {
      return visibleTasks.filter((task) => this.isTaskInActiveTreeFocus(task));
    }

    if (this.activeSegmentKey || this.activeModelKey) {
      return visibleTasks.filter((task) => this.isTaskInActiveTreeFocus(task));
    }

    return [];
  }

  private resolveRenderDomain(visibleTasks: TaskItem[]): { min: number; max: number } {
    if (!this.currentDomain) {
      return { min: Date.now(), max: Date.now() + 30 * DAY_MS };
    }

    const focusedTasks = this.resolveFocusedTasks(visibleTasks);
    if (!focusedTasks.length) {
      return this.currentDomain;
    }

    const focusMin = Math.min(...focusedTasks.map((task) => task.start));
    const focusMax = Math.max(...focusedTasks.map((task) => task.end));
    const focusSpan = Math.max(DAY_MS, focusMax - focusMin);
    const pad = focusSpan * 0.3;
    let min = focusMin - pad;
    let max = focusMax + pad;

    const minWindowMs = this.resolveFocusMinWindowMs();
    const windowSpan = max - min;
    if (windowSpan < minWindowMs) {
      const center = (focusMin + focusMax) / 2;
      min = center - minWindowMs / 2;
      max = center + minWindowMs / 2;
    }

    min = Math.max(this.currentDomain.min, min);
    max = Math.min(this.currentDomain.max, max);
    if (max <= min) {
      return this.currentDomain;
    }
    return { min, max };
  }

  private resolveFocusMinWindowMs(): number {
    const configuredDays = Number(this.data?.chartConfig?.focusMinDays ?? 120);
    const days = Number.isFinite(configuredDays) && configuredDays > 0 ? configuredDays : 120;
    return days * DAY_MS;
  }

  private scrollFocusIntoView(
    visibleTasks: TaskItem[],
    renderDomain: { min: number; max: number },
  ): void {
    const shell = this.chartShell?.nativeElement;
    if (!shell) {
      return;
    }

    const focusedTasks = this.resolveFocusedTasks(visibleTasks);
    if (!focusedTasks.length) {
      const initialAnchor = this.resolveInitialViewportAnchor(renderDomain);
      if (initialAnchor === null) {
        shell.scrollLeft = 0;
        return;
      }
      this.scrollShellToTimelineAnchor(shell, renderDomain, initialAnchor);
      return;
    }

    const anchorTask = focusedTasks[0];
    this.scrollShellToTimelineAnchor(shell, renderDomain, anchorTask.start, visibleTasks);

    const contentHeightPx = this.toPixels(this.resolvedChartContentHeight);
    if (!contentHeightPx || visibleTasks.length < 2) {
      return;
    }
    const rowIndex = Math.max(
      0,
      visibleTasks.findIndex((task) => task.selectionKey === anchorTask.selectionKey),
    );
    if (this.shouldUseInternalVerticalScroll()) {
      const visibleRows = this.resolveVisibleRowWindowSize(visibleTasks.length);
      if (visibleRows >= visibleTasks.length) {
        return;
      }

      const startValue = Math.max(0, Math.min(rowIndex, visibleTasks.length - visibleRows));
      const endValue = startValue + visibleRows - 1;
      this.myChart?.dispatchAction({
        type: 'dataZoom',
        dataZoomId: 'ganttYSlider',
        startValue,
        endValue,
      });
      return;
    }

    const rowBand = contentHeightPx / visibleTasks.length;
    const yTarget = Math.max(0, Math.round(rowIndex * rowBand - rowBand));
    const maxScrollTop = Math.max(0, shell.scrollHeight - shell.clientHeight);
    shell.scrollTop = Math.min(maxScrollTop, yTarget);
  }

  private resolveInitialViewportAnchor(renderDomain: { min: number; max: number }): number | null {
    const configuredDate =
      this.data?.chartConfig?.initialViewportDate ?? this.data?.initialViewportDate;
    if (configuredDate !== undefined && configuredDate !== null && configuredDate !== '') {
      const parsed = this.parseDate(configuredDate, false);
      if (parsed !== null) {
        return Math.max(renderDomain.min, Math.min(renderDomain.max, parsed));
      }
    }

    const configuredYear = Number(
      this.data?.chartConfig?.initialViewportYear ?? this.data?.initialViewportYear,
    );
    if (Number.isFinite(configuredYear) && configuredYear >= 1000 && configuredYear <= 9999) {
      const yearStart = this.startOfYear(new Date(Math.trunc(configuredYear), 0, 1).getTime());
      return Math.max(renderDomain.min, Math.min(renderDomain.max, yearStart));
    }

    return null;
  }

  private scrollShellToTimelineAnchor(
    shell: HTMLDivElement,
    renderDomain: { min: number; max: number },
    anchorTime: number,
    visibleTasks: TaskItem[] = [],
  ): void {
    const hostWidth =
      this.chartHost?.nativeElement?.clientWidth || this.chartHost?.nativeElement?.scrollWidth || 0;
    const left = this.useSideTreeMode() ? 18 : this.resolveLeft(visibleTasks);
    const timelineSpan = Math.max(DAY_MS, renderDomain.max - renderDomain.min);
    const ratio = Math.min(1, Math.max(0, (anchorTime - renderDomain.min) / timelineSpan));
    const xInsideHost = left + ratio * Math.max(1, hostWidth - left - 24);
    const leftTarget = Math.max(0, Math.round(xInsideHost - 56));
    const maxScrollLeft = Math.max(0, shell.scrollWidth - shell.clientWidth);
    shell.scrollLeft = Math.min(maxScrollLeft, leftTarget);
  }

  private resolveConfiguredLegendItems(): LegendConfigItem[] {
    const rawItems = this.data?.chartConfig?.legendItems;
    if (!Array.isArray(rawItems)) {
      return [];
    }

    return rawItems
      .map((item: any): LegendConfigItem | null => {
        const label = String(item?.label ?? '').trim();
        const color = String(item?.color ?? '').trim();
        if (!label || !color) {
          return null;
        }

        const keys: string[] = Array.isArray(item?.keys)
          ? item.keys.map((key: any) => String(key ?? '').trim()).filter((key: string) => !!key)
          : [];

        return { label, color, keys };
      })
      .filter((item): item is LegendConfigItem => item !== null);
  }

  private matchesLegend(task: TaskItem, legendLabel: string): boolean {
    const configured = this.resolveConfiguredLegendItems();
    if (!configured.length) {
      return this.normalizeLegendToken(task.colorKey) === this.normalizeLegendToken(legendLabel);
    }

    const item = configured.find((entry) => entry.label === legendLabel);
    if (!item) {
      return false;
    }

    const normalizedTaskColorKey = this.normalizeLegendToken(task.colorKey);
    const normalizedTaskStatus = this.normalizeLegendToken(task.status);

    if (item.keys?.length) {
      const normalizedLegendKeys = item.keys.map((key) => this.normalizeLegendToken(key));
      if (
        normalizedLegendKeys.includes(normalizedTaskColorKey) ||
        normalizedLegendKeys.includes(normalizedTaskStatus)
      ) {
        return true;
      }
    }

    // Fallback: when keys are missing/incomplete, match using rendered bar color.
    return this.normalizeColorToken(task.color) === this.normalizeColorToken(item.color);
  }

  private normalizeLegendToken(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private matchesLineStyleLegend(task: TaskItem, style: LineStyleLegendKey): boolean {
    const isInactive = this.isInactiveByStatus(this.normalizeFilterToken(task.status));
    return style === 'dotted' ? isInactive : !isInactive;
  }

  private normalizeColorToken(color: unknown): string {
    return String(color ?? '')
      .replace(/\s+/g, '')
      .trim()
      .toLowerCase();
  }

  private applyRowFilters(rows: TaskRecord[], filters?: Record<string, unknown>): TaskRecord[] {
    if (this.data?.requestPayload && this.data?.chartConfig?.applyClientFilters !== true) {
      return rows;
    }

    if (!rows.length || !filters || typeof filters !== 'object') {
      return rows;
    }

    const normalizedFilters = this.remapFilterKeys(filters);
    const segmentFilterKey = this.resolveSegmentFilterKey();
    const preserveAllSegments = this.shouldPreserveAllSegmentsInTree();
    const filterableKeys = this.resolveFilterableKeys().filter(
      (key) => !(preserveAllSegments && key === segmentFilterKey),
    );
    let filteredRows = rows;

    if (filterableKeys.length) {
      const hasRelevantFilters = filterableKeys.some((key) => {
        const selectedValues = this.normalizeFilterValues(normalizedFilters[key]);
        return selectedValues.length > 0 && !selectedValues.includes('all');
      });
      if (hasRelevantFilters) {
        filteredRows = filteredRows.filter((row) =>
          this.matchesAllRowFilters(row, normalizedFilters, filterableKeys),
        );
      }
    }

    return this.applyNumericRangeFilters(filteredRows, normalizedFilters);
  }

  private resolveFilterableKeys(): string[] {
    const configured = this.data?.chartConfig?.filterableKeys || this.data?.filterableKeys;
    if (!Array.isArray(configured)) {
      return [];
    }
    return configured
      .map((value: unknown) => String(value || '').trim())
      .filter((value: string) => !!value);
  }

  private matchesAllRowFilters(
    row: TaskRecord,
    filters: Record<string, unknown>,
    filterableKeys: string[],
  ): boolean {
    return filterableKeys.every((key) => {
      const selectedValues = this.normalizeFilterValues(filters[key]);
      if (!selectedValues.length || selectedValues.includes('all')) {
        return true;
      }

      const rowValue = this.byPath(row, key) ?? row[key];
      if (rowValue === undefined || rowValue === null || rowValue === '') {
        return false;
      }

      if (Array.isArray(rowValue)) {
        return rowValue.some((value) => selectedValues.includes(this.normalizeFilterToken(value)));
      }

      return selectedValues.includes(this.normalizeFilterToken(rowValue));
    });
  }

  private normalizeFilterValues(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    const tokens = value
      .flat(Infinity)
      .map((item) => this.normalizeFilterToken(item))
      .filter((token) => !!token);

    return Array.from(new Set(tokens));
  }

  private normalizeFilterToken(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private extractGlobalServiceFilters(source: any): Record<string, unknown> {
    const filters = (source?.filter || {}) as Record<string, unknown>;
    return this.remapFilterKeys(filters);
  }

  private extractGlobalFilterServiceFilters(source: any): Record<string, unknown> {
    const requestParams =
      source?.requestParams && typeof source.requestParams === 'object' ? source.requestParams : {};
    const queryParams =
      source?.queryParams && typeof source.queryParams === 'object' ? source.queryParams : {};
    return this.remapFilterKeys({
      ...(requestParams as Record<string, unknown>),
      ...(queryParams as Record<string, unknown>),
    });
  }

  private mergeActiveFilters(): Record<string, unknown> {
    return {
      ...this.activeGlobalServiceFilters,
      ...this.activeGlobalFilterServiceFilters,
    };
  }

  private resolveSegmentFilterKey(): string {
    return String(
      this.data?.chartConfig?.segmentKey || this.data?.segmentKey || 'cleaned_vertical',
    ).trim();
  }

  private shouldPreserveAllSegmentsInTree(): boolean {
    return (
      this.useSideTreeMode() &&
      (this.data?.chartConfig?.sideTreePreserveAllSegmentsOnSegmentFilter === true ||
        this.data?.sideTreePreserveAllSegmentsOnSegmentFilter === true)
    );
  }

  private remapFilterKeys(filters: Record<string, unknown>): Record<string, unknown> {
    const remapped: Record<string, unknown> = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
      const targetKey = FILTER_KEY_ALIASES[key] || key;
      remapped[targetKey] = value;
      if (targetKey === 'investment_estimate') {
        remapped['simulation_investment_estimate'] = value;
      }
      if (targetKey === 'final_score') {
        remapped['simulation_final_score'] = value;
      }
    });
    return remapped;
  }

  private applyNumericRangeFilters(
    rows: TaskRecord[],
    filters: Record<string, unknown>,
  ): TaskRecord[] {
    const investmentRange = this.toNumericRange(
      filters['investment_estimate'] ?? filters['simulation_investment_estimate'],
    );
    const scoreRange = this.toNumericRange(
      filters['final_score'] ?? filters['simulation_final_score'],
    );
    if (!investmentRange && !scoreRange) {
      return rows;
    }

    return rows.filter((row) => {
      if (investmentRange) {
        const value = Number(
          this.byPath(row, 'investment_estimate') ??
            row['investment_estimate'] ??
            this.byPath(row, 'simulation_investment_estimate') ??
            row['simulation_investment_estimate'],
        );
        if (!Number.isFinite(value) || value < investmentRange.min || value > investmentRange.max) {
          return false;
        }
      }

      if (scoreRange) {
        const value = Number(
          this.byPath(row, 'final_score') ??
            row['final_score'] ??
            this.byPath(row, 'simulation_final_score') ??
            row['simulation_final_score'],
        );
        if (!Number.isFinite(value) || value < scoreRange.min || value > scoreRange.max) {
          return false;
        }
      }

      return true;
    });
  }

  private toNumericRange(value: unknown): { min: number; max: number } | null {
    if (!Array.isArray(value)) {
      return null;
    }

    const numericValues = value
      .flat(Infinity)
      .map((entry) => Number(entry))
      .filter((entry) => Number.isFinite(entry));

    if (numericValues.length < 2) {
      return null;
    }

    const first = numericValues[0];
    const second = numericValues[1];
    return {
      min: Math.min(first, second),
      max: Math.max(first, second),
    };
  }

  private resolveConfiguredKey(key: string): string | undefined {
    const chartConfigValue = this.data?.chartConfig?.[key];
    if (typeof chartConfigValue === 'string' && chartConfigValue.trim()) {
      return chartConfigValue.trim();
    }

    const dataValue = this.data?.[key];
    if (typeof dataValue === 'string' && dataValue.trim()) {
      return dataValue.trim();
    }

    return undefined;
  }

  private resolveInBarLabelKey(): string | undefined {
    const configuredCandidates = [
      this.data?.chartConfig?.taskLabelInBarKey,
      this.data?.chartConfig?.dataLabelKey,
      this.data?.chartConfig?.dataLabel,
      this.data?.chartConfig?.dataLablel,
      this.data?.taskLabelInBarKey,
      this.data?.dataLabelKey,
      this.data?.dataLabel,
      this.data?.dataLablel,
    ];

    for (const candidate of configuredCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return undefined;
  }

  private resolveTaskBarLabel(
    row: TaskRecord,
    context: { name: string; group: string; status: string; owner: string; progress: number | null },
  ): string {
    const configuredKey = this.resolveInBarLabelKey();
    if (!configuredKey) {
      return '';
    }

    const normalized = configuredKey.toLowerCase();
    const taskNameKey = this.resolveConfiguredKey('taskNameKey');
    const groupKey = this.resolveConfiguredKey('groupKey');
    const statusKey = this.resolveConfiguredKey('statusKey');
    const ownerKey = this.resolveConfiguredKey('ownerKey');
    const progressKey = this.resolveConfiguredKey('progressKey');

    if (
      this.matchesConfiguredKey(configuredKey, taskNameKey) ||
      ['name', 'task', 'label', 'title'].includes(normalized)
    ) {
      return context.name;
    }

    if (
      this.matchesConfiguredKey(configuredKey, groupKey) ||
      ['group', 'track', 'stream', 'category'].includes(normalized)
    ) {
      return context.group;
    }

    if (
      this.matchesConfiguredKey(configuredKey, statusKey) ||
      ['status', 'phase', 'state'].includes(normalized)
    ) {
      return context.status;
    }

    if (
      this.matchesConfiguredKey(configuredKey, ownerKey) ||
      ['owner', 'team', 'assignee'].includes(normalized)
    ) {
      return context.owner;
    }

    if (
      this.matchesConfiguredKey(configuredKey, progressKey) ||
      ['progress', 'completion'].includes(normalized)
    ) {
      return context.progress === null ? '' : `${context.progress}%`;
    }

    return this.stringifyTaskBarLabel(this.readValue(row, configuredKey, [configuredKey]));
  }

  private logTaskLabelDebug(rows: TaskRecord[], tasks: TaskItem[]): void {
    const configuredKey = this.resolveInBarLabelKey();
    if (!configuredKey) {
      return;
    }

    const sampleRow = rows[0];
    // console.log('[GanttChart] label resolution', {
    //   widgetId: this.data?.widgetId,
    //   configuredKey,
    //   rowCount: rows.length,
    //   taskCount: tasks.length,
    //   sampleRowKeys: sampleRow ? Object.keys(sampleRow).slice(0, 20) : [],
    //   sampleRawValue: sampleRow ? this.byPath(sampleRow, configuredKey) : undefined,
    //   sampleTasks: tasks.slice(0, 3).map((task) => ({
    //     name: task.name,
    //     dataLabel: task.dataLabel,
    //     rowLabel: task.rowLabel,
    //   })),
    // });
  }

  private readValue(row: TaskRecord, configured: string | undefined, fallbacks: string[]): unknown {
    const keys = [configured, ...fallbacks].filter((key): key is string => !!key);
    for (const key of keys) {
      const value = this.byPath(row, key);
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
  }

  private readString(row: TaskRecord, configured: string | undefined, fallbacks: string[]): string {
    const value = this.readValue(row, configured, fallbacks);
    return value === undefined || value === null ? '' : String(value).trim();
  }

  private stringifyTaskBarLabel(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value) : '';
    }

    return String(value).trim();
  }

  private parseProgress(value: unknown): number | null {
    if (value === undefined || value === null || value === '') return null;
    const numeric = Number(String(value).replace('%', '').trim());
    return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : null;
  }

  private parseDate(value: unknown, treatAsEnd: boolean): number | null {
    if (value === undefined || value === null || value === '') return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number' && Number.isFinite(value)) {
      if (value >= 1000 && value <= 9999) {
        return treatAsEnd
          ? this.endOfYear(new Date(value, 0, 1).getTime())
          : this.startOfYear(new Date(value, 0, 1).getTime());
      }
      return Math.abs(value) < 1e11 ? value * 1000 : value;
    }
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if (/^\d{13}$/.test(text)) return Number(text);
    if (/^\d{10}$/.test(text)) return Number(text) * 1000;
    if (/^\d{4}$/.test(text)) {
      const year = Number(text);
      return treatAsEnd
        ? this.endOfYear(new Date(year, 0, 1).getTime())
        : this.startOfYear(new Date(year, 0, 1).getTime());
    }

    const quarterFirstMatch = text.match(/^Q([1-4])\s*[-/_]?\s*(\d{4})$/i);
    const yearFirstMatch = text.match(/^(\d{4})\s*[-/_]?\s*Q([1-4])$/i);
    if (quarterFirstMatch || yearFirstMatch) {
      const year = quarterFirstMatch ? Number(quarterFirstMatch[2]) : Number(yearFirstMatch?.[1]);
      const quarter = quarterFirstMatch
        ? Number(quarterFirstMatch[1])
        : Number(yearFirstMatch?.[2]);
      const quarterDate = new Date(year, (quarter - 1) * 3, 1).getTime();
      return treatAsEnd ? this.endOfQuarter(quarterDate) : this.startOfQuarter(quarterDate);
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const parsed = Date.parse(`${text}T00:00:00`);
      return Number.isNaN(parsed)
        ? null
        : treatAsEnd
          ? this.endOfDay(parsed)
          : this.startOfDay(parsed);
    }
    const parsed = Date.parse(text);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private formatDuration(start: number, end: number): string {
    const totalDays = Math.max(
      1,
      Math.round((this.endOfDay(end) - this.startOfDay(start)) / DAY_MS) + 1,
    );

    if (totalDays >= 365) {
      const years = totalDays / 365;
      return `${years.toFixed(years >= 2 ? 0 : 1)} years`;
    }
    if (totalDays >= 90) {
      const quarters = totalDays / 91;
      return `${quarters.toFixed(1)} quarters`;
    }
    if (totalDays >= 30) {
      const months = totalDays / 30;
      return `${months.toFixed(1)} months`;
    }
    if (totalDays >= 7) {
      const weeks = totalDays / 7;
      return `${weeks.toFixed(1)} weeks`;
    }
    return `${totalDays} days`;
  }

  private byPath(source: any, path: string): any {
    return path
      .split('.')
      .reduce(
        (current: any, key: string) =>
          current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key],
        source,
      );
  }

  private formatDate(value: number): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(value));
  }

  private startOfDay(value: number): number {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  private endOfDay(value: number): number {
    const date = new Date(value);
    date.setHours(23, 59, 59, 999);
    return date.getTime();
  }

  private startOfWeek(value: number): number {
    const date = new Date(this.startOfDay(value));
    const diff = date.getDay() === 0 ? -6 : 1 - date.getDay();
    date.setDate(date.getDate() + diff);
    return this.startOfDay(date.getTime());
  }

  private endOfWeek(value: number): number {
    return this.endOfDay(this.startOfWeek(value) + 6 * DAY_MS);
  }

  private startOfMonth(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), 1).getTime();
  }

  private endOfMonth(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
  }

  private startOfQuarter(value: number): number {
    const date = new Date(value);
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterStartMonth, 1).getTime();
  }

  private endOfQuarter(value: number): number {
    const date = new Date(value);
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    return new Date(date.getFullYear(), quarterStartMonth + 3, 0, 23, 59, 59, 999).getTime();
  }

  private startOfYear(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), 0, 1).getTime();
  }

  private endOfYear(value: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999).getTime();
  }

  private addMonths(value: number, monthsToAdd: number): number {
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth() + monthsToAdd, 1).getTime();
  }

  private isoWeek(date: Date): { week: number; year: number } {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = utc.getUTCDay() || 7;
    utc.setUTCDate(utc.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((utc.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
    return { week, year: utc.getUTCFullYear() };
  }

  private withAlpha(color: string, alpha: number): string {
    const normalized = color.trim();

    if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) {
      let hex = normalized.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => char + char)
          .join('');
      }

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const rgbMatch = normalized.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const parts = rgbMatch[1].split(',').map((part) => part.trim());
      const [r = '0', g = '0', b = '0'] = parts;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return normalized;
  }

  private wrapText(text: string, maxCharsPerLine: number, maxLines: number): string {
    if (text.length <= maxCharsPerLine) {
      return text;
    }

    // With only 1 line allowed, just truncate directly — no wrapping needed
    if (maxLines <= 1) {
      return this.truncateText(text, maxCharsPerLine);
    }

    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) {
      return this.truncateText(text, maxCharsPerLine);
    }

    const lines: string[] = [];
    let currentLine = '';

    for (let index = 0; index < words.length; index += 1) {
      const word = words[index];
      const candidate = currentLine ? `${currentLine} ${word}` : word;

      if (candidate.length <= maxCharsPerLine) {
        currentLine = candidate;
        continue;
      }

      if (!currentLine) {
        lines.push(this.truncateText(word, maxCharsPerLine));
      } else {
        lines.push(currentLine);
        currentLine = word;
      }

      if (lines.length === maxLines - 1) {
        const remaining = [currentLine, ...words.slice(index + 1)].filter(Boolean).join(' ');
        lines.push(this.truncateText(remaining, maxCharsPerLine));
        return lines.join('\n');
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.slice(0, maxLines).join('\n');
  }

  private truncateText(text: string, maxChars: number): string {
    const normalized = text.trim();
    if (normalized.length <= maxChars) {
      return normalized;
    }

    if (maxChars <= 1) {
      return '...';
    }

    return `${normalized.slice(0, Math.max(1, maxChars - 3)).trimEnd()}...`;
  }

  private fitTextToBar(text: string, widthPx: number, fontSize: number): string {
    const normalized = String(text || '').trim();
    if (!normalized) {
      return '';
    }

    // Custom-series text overflow can fail to render; truncate text ourselves.
    const maxChars = Math.max(1, Math.floor(widthPx / Math.max(6, fontSize * 0.58)));
    if (normalized.length <= maxChars) {
      return normalized;
    }

    if (maxChars <= 3) {
      return normalized.slice(0, 1);
    }

    return `${normalized.slice(0, Math.max(1, maxChars - 3)).trimEnd()}...`;
  }

  private toPixels(value: string): number | null {
    const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
    return match ? Number(match[1]) : null;
  }

  private getChartWidth(): number {
    return (
      this.myChart?.getWidth() ||
      this.chartHost?.nativeElement?.clientWidth ||
      (typeof window !== 'undefined' ? window.innerWidth : 1024)
    );
  }

  private getDefaultColor(index: number): string {
    return DEFAULT_CHART_COLORS[Math.abs(index) % DEFAULT_CHART_COLORS.length];
  }

  private getContrastingTextColor(color: string): string {
    const rgb = this.toRgb(color);
    if (!rgb) {
      return '#ffffff';
    }
    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#0f172a' : '#ffffff';
  }

  private toRgb(color: string): [number, number, number] | null {
    const normalized = color.trim();
    if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) {
      let hex = normalized.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => `${char}${char}`)
          .join('');
      }
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }

    const rgbMatch = normalized.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbMatch) {
      return null;
    }

    const parts = rgbMatch[1]
      .split(',')
      .slice(0, 3)
      .map((part) => Number(part.trim()));
    if (parts.some((value) => !Number.isFinite(value))) {
      return null;
    }
    return [parts[0], parts[1], parts[2]];
  }

  private buildTooltipConfig(
    tooltipConfig: Record<string, unknown> | undefined,
  ): echarts.TooltipComponentOption {
    const resolvedConfig: echarts.TooltipComponentOption = {
      trigger: 'item',
      confine: true,
      ...((tooltipConfig && typeof tooltipConfig === 'object'
        ? tooltipConfig
        : {}) as echarts.TooltipComponentOption),
    };

    if (resolvedConfig.enterable === undefined) {
      resolvedConfig.enterable = false;
    }

    const tooltipInteractionCss = 'pointer-events:none;user-select:none;caret-color:transparent;';
    const existingExtraCssText = String(resolvedConfig.extraCssText || '').trim();
    if (existingExtraCssText) {
      if (!existingExtraCssText.includes('pointer-events')) {
        resolvedConfig.extraCssText = `${existingExtraCssText};${tooltipInteractionCss}`;
      }
    } else {
      resolvedConfig.extraCssText = tooltipInteractionCss;
    }

    if (!resolvedConfig.formatter) {
      resolvedConfig.formatter = (params: any) => this.defaultTooltipFormatter(params);
    }

    return resolvedConfig;
  }

  private defaultTooltipFormatter(params: any): string {
    const marker = typeof params?.marker === 'string' ? params.marker : '';
    const task = params?.data?.taskData as TaskItem | undefined;
    const rawTitle =
      params?.data?.taskName ??
      params?.data?.task ??
      params?.data?.taskLabelInBar ??
      params?.name ??
      '';
    const title = echarts.format.encodeHTML(String(rawTitle || '').trim() || '-');
    const date = echarts.format.encodeHTML(this.resolveTooltipDateLabel(params));
    const segmentValue = echarts.format.encodeHTML(
      String(task?.segment || params?.data?.category || 'Unspecified'),
    );
    const engineModelValue = echarts.format.encodeHTML(
      String(task?.engineModel || 'Unspecified'),
    );

    return `${marker} ${title}<br/>${date}<br/>${segmentValue}<br/>${engineModelValue}`;
  }

  private resolveTooltipDateLabel(params: any): string {
    const start = Number(params?.data?.start ?? params?.value?.[1]);
    const end = Number(params?.data?.end ?? params?.value?.[2]);

    if (Number.isFinite(start) && Number.isFinite(end)) {
      if (this.startOfDay(start) === this.startOfDay(end)) {
        return this.formatDate(start);
      }
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    }

    if (Number.isFinite(start)) {
      return this.formatDate(start);
    }

    if (Number.isFinite(end)) {
      return this.formatDate(end);
    }

    return '-';
  }
}




